import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from './supabaseClient.js';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ConfirmModal from './ConfirmModal.js';
import JobFormModal from './JobFormModal.js';
import TransitFormModal from './TransitFormModal.js';
import UnassignedJobsPanel from './UnassignedJobsPanel.js';
import MoveDayModal from './MoveDayModal.js';
import EditRouteModal from './EditRouteModal.js';
import PushRouteModal from './PushRouteModal.js';
import DayRouteMapModal, { isMappableAddress } from './DayRouteMapModal.js';
import CurrentLocationButton from './CurrentLocationButton.js';
import { JobExpandHintProvider, useHintHover } from './JobExpandHint.js';
import { useFeatureTour } from './FeatureTour.js';
import { calculateTravelForDays, computeTransitTravel } from './googleMapsTravel.js';
import { optimizeRoute, optimizeDay } from './routeOptimizer.js';
import { StatusBadge } from './bookingStatus.js';
import WeatherStrip from './WeatherStrip.js';
import ClockEntryModal from './ClockEntryModal.js';
import DayNotesModal from './DayNotesModal.js';
import { workedHours, formatInOriginZone } from './timeEntryHours.js';

// ─── Helpers ──────────────────────────────────────────────

const formatDate = (startDate, dayNumber) => {
  if (!startDate) return `Day ${dayNumber}`;
  const d = new Date(startDate + 'T00:00:00');
  d.setDate(d.getDate() + dayNumber - 1);
  return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
};

const getDayHours = (items) => {
  return items.reduce((sum, item) => {
    if (item.row_type === 'JOB') return sum + (parseFloat(item.estimated_hours) || 0);
    if (item.row_type === 'TRAVEL' || item.row_type === 'TRANSIT') return sum + (parseFloat(item.travel_hours) || 0);
    return sum;
  }, 0);
};

// ─── Actual-hours helpers (time_entries layer) ──────────────
// A clock record only becomes "actual" once both ends exist. Worked hours are the
// diff of the two stored UTC instants — never wall-time subtraction (see timeEntryHours.js).
const isCompleteEntry = (e) => !!(e && e.clock_in_at && e.clock_out_at);
const entryActualHours = (e) => (isCompleteEntry(e) ? workedHours(e.clock_in_at, e.clock_out_at) : null);

// Business rule: only this applicator (Grant) tracks payroll hours here — Steve &
// Dallon use a separate time system. Keyed off the applicator id (stable across renames).
const HOURS_EMPLOYEE_ID = '5cd63d77-cc02-4638-9fe6-39cf2b6d2c69';

// Find the most recent location-bearing item in a day before a given sort_order.
// Skips TRAVEL rows (their destinations are derivative). Used to seed Transit origin.
const findPriorEndpoint = (dayItems, sortOrder, homeBase) => {
  const prior = (dayItems || [])
    .filter((i) => (i.sort_order || 0) < sortOrder)
    .sort((a, b) => (b.sort_order || 0) - (a.sort_order || 0));
  for (const item of prior) {
    if (item.row_type === 'JOB' && item.location) return item.location;
    if (item.row_type === 'TRANSIT' && item.travel_destination) return item.travel_destination;
  }
  return homeBase || null;
};

const getHourColor = (hours, limit) => {
  if (hours > limit) return 'text-red-300';
  if (hours > limit - 0.5) return 'text-amber-400';
  return 'text-green-400';
};

const getCalendarDate = (startDate, dayNumber) => {
  const d = new Date(startDate + 'T00:00:00');
  d.setDate(d.getDate() + dayNumber - 1);
  return d;
};

// Calendar date of a route day as 'YYYY-MM-DD' — the work_date key for time_entries.
// Matches the holiday-matching convention already used in DayColumn.
const dayToISODate = (startDate, dayNumber) => {
  if (!startDate) return null;
  return getCalendarDate(startDate, dayNumber).toISOString().split('T')[0];
};

// Calendar week index (1-based) of a route day, aligned to Sun–Sat weeks.
const getWeekNumber = (startDate, dayNumber) => {
  if (!startDate) return Math.ceil(dayNumber / 7);
  const startDow = new Date(startDate + 'T00:00:00').getDay(); // 0=Sun … 6=Sat
  return Math.floor((startDow + dayNumber - 1) / 7) + 1;
};

const isWeekend = (startDate, dayNumber) => {
  if (!startDate) return false;
  const d = getCalendarDate(startDate, dayNumber);
  return d.getDay() === 0 || d.getDay() === 6;
};

const formatDateRange = (startDate, dayNumbers) => {
  if (!startDate || dayNumbers.length === 0) return '';
  const first = getCalendarDate(startDate, dayNumbers[0]);
  const last = getCalendarDate(startDate, dayNumbers[dayNumbers.length - 1]);
  const fmt = (d) => d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  return `${fmt(first)} – ${fmt(last)}`;
};

// ─── Week Section ─────────────────────────────────────────

const WeekSection = ({ weekNumber, dateRange, children, items, applicator, entryForDay }) => {
  const [collapsed, setCollapsed] = useState(false);

  const weekJobs = items.filter((i) => i.row_type === 'JOB');
  const weekValue = weekJobs.reduce((sum, i) => sum + (parseFloat(i.value) || 0), 0);

  // Week hours: actual worked time for fully-clocked days, schedule estimate otherwise.
  const weekDayNums = [...new Set(items.map((i) => i.day_number).filter((n) => n != null))];
  let weekHours = 0;
  let anyActual = false;
  for (const dn of weekDayNums) {
    const dayItems = items.filter((i) => i.day_number === dn);
    const entry = entryForDay ? entryForDay(dn) : null;
    if (isCompleteEntry(entry)) { weekHours += entryActualHours(entry); anyActual = true; }
    else weekHours += getDayHours(dayItems);
  }

  return (
    <div className="mb-6">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex flex-wrap items-center gap-x-3 gap-y-1 w-full text-left mb-3 group"
      >
        <span className="text-gray-400 text-xs group-hover:text-white transition-colors">
          {collapsed ? '\u25B6' : '\u25BC'}
        </span>
        <h2 className="text-lg font-bold text-white">Week {weekNumber}</h2>
        {dateRange && <span className="text-sm text-gray-400 hidden sm:inline">{dateRange}</span>}
        <span className="text-xs text-gray-400">
          {weekJobs.length} jobs &middot; {weekHours.toFixed(1)}h{anyActual && ' (incl. actual)'} &middot; ${weekValue.toLocaleString()}
        </span>
        {applicator && (
          <span className="text-xs text-gray-400 sm:ml-auto hidden sm:inline">
            {applicator.name}{applicator.home_base_location ? ` — ${applicator.home_base_location.split(',')[0]}` : ''}
          </span>
        )}
      </button>
      {!collapsed && <div className="space-y-4">{children}</div>}
    </div>
  );
};

// ─── Blank pre-start day cell ─────────────────────────────
// A calendar day that sits before the route's start_date (e.g. the Sunday ahead of a
// Monday start). Not yet a work day; clicking "Include" rolls the start back to cover it.
const BlankDayCell = ({ startDate, preStartOffset, onInclude }) => {
  if (!startDate) return null;
  const d = new Date(startDate + 'T00:00:00');
  d.setDate(d.getDate() - preStartOffset);
  const label = d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
  const isWeekendDay = d.getDay() === 0 || d.getDay() === 6;
  return (
    <div className="bg-gray-700/20 border border-dashed border-gray-500/40 rounded-lg p-4 flex flex-col gap-2">
      <span className={`text-sm font-medium ${isWeekendDay ? 'text-amber-400' : 'text-gray-400'}`}>
        {label}{isWeekendDay && ' (weekend)'}
      </span>
      <p className="text-xs text-gray-400">Outside the route — not a work day yet.</p>
      <button
        onClick={() => onInclude(preStartOffset)}
        title="Roll the route's start date back to include this day. Existing work keeps its dates."
        className="self-start text-xs px-2 py-1 rounded bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
      >
        + Include this day
      </button>
    </div>
  );
};

// ─── Pin Toggle Inline Form ──────────────────────────────

const PinForm = ({ item, onSave, onCancel, saveLabel = 'Pin Job' }) => {
  const [pinTime, setPinTime] = useState(item.pin_time || '');
  const [pinNotes, setPinNotes] = useState(item.pin_notes || '');

  return (
    <div className="mt-2 pt-2 border-t border-gray-500/30 space-y-2">
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="block text-xs text-gray-400 mb-0.5">Arrival Time</label>
          <input
            type="time"
            value={pinTime}
            onChange={(e) => setPinTime(e.target.value)}
            className="w-full px-2 py-1 bg-gray-900 border border-gray-500 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-orange-400"
          />
        </div>
        <div className="flex-[2]">
          <label className="block text-xs text-gray-400 mb-0.5">Pin Reason</label>
          <input
            type="text"
            value={pinNotes}
            onChange={(e) => setPinNotes(e.target.value)}
            placeholder="e.g. Meet site foreman before 9am"
            className="w-full px-2 py-1 bg-gray-900 border border-gray-500 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-orange-400"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="text-xs text-gray-400 hover:text-white">Cancel</button>
        <button
          onClick={() => onSave(pinTime || null, pinNotes || null)}
          className="text-xs px-2 py-1 bg-orange-400 text-gray-900 font-medium rounded hover:bg-orange-300"
        >
          {saveLabel}
        </button>
      </div>
    </div>
  );
};

// ─── Day Extension Inline Form ────────────────────────────

const DayExtensionForm = ({ currentOverride, onSave, onCancel }) => {
  const [hours, setHours] = useState(currentOverride || 10);

  return (
    <div className="flex items-center gap-2 mt-2">
      <label className="text-xs text-gray-300">Max hours:</label>
      <input
        type="number"
        step="0.5"
        min="8"
        max="16"
        value={hours}
        onChange={(e) => setHours(e.target.value)}
        className="w-20 px-2 py-1 bg-gray-900 border border-gray-500 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-amber-400"
      />
      <button onClick={() => onSave(parseFloat(hours))} className="text-xs px-2 py-1 bg-amber-400 text-gray-900 font-medium rounded hover:bg-amber-300">
        Set
      </button>
      <button onClick={onCancel} className="text-xs text-gray-400 hover:text-white">Cancel</button>
    </div>
  );
};

// ─── Item Card (Sortable Wrapper) ─────────────────────────

const SortableItemCard = ({ item, route, onTogglePin, onEditJob, onEditTransit, onDeleteItem, isStaleTravel, onTravelToggle, homeBase }) => {
  const isDraggable = (item.row_type === 'JOB' || item.row_type === 'TRANSIT') && !item.is_pinned;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: !isDraggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const movedStyle = item._justMoved ? {
    ...style,
    boxShadow: '0 0 0 2px rgba(14, 159, 110, 0.6)',
    borderRadius: '6px',
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  } : style;

  return (
    <div ref={setNodeRef} style={movedStyle} {...attributes}>
      <ItemCard
        item={item}
        route={route}
        dragListeners={isDraggable ? listeners : null}
        onTogglePin={onTogglePin}
        onEditJob={onEditJob}
        onEditTransit={onEditTransit}
        onDeleteItem={onDeleteItem}
        isStaleTravel={isStaleTravel}
        onTravelToggle={onTravelToggle}
        homeBase={homeBase}
      />
    </div>
  );
};

// ─── Travel Row (toggle home/hotel, inline editable hours & origin) ──

const isStartOfDayType = (t) => t === 'start_of_day_home' || t === 'start_of_day_hotel' || t === 'home_base' || t === 'start_of_week_from_home';
const isEndOfDayType = (t) => t === 'end_of_day_home' || t === 'end_of_day_hotel' || t === 'end_of_week_home';
const isHomeType = (t) => t === 'start_of_day_home' || t === 'end_of_day_home' || t === 'home_base' || t === 'start_of_week_from_home' || t === 'end_of_week_home';
const isHotelType = (t) => t === 'start_of_day_hotel' || t === 'end_of_day_hotel';

const TravelRow = ({ item, isStaleTravel, onTravelToggle, homeBase }) => {
  const [editing, setEditing] = useState(false);
  const [editHours, setEditHours] = useState(item.travel_hours || '');
  const [editingOrigin, setEditingOrigin] = useState(false);
  const [editOrigin, setEditOrigin] = useState('');

  // Feature tour: this row may be the one the announcement is pointing at.
  const { stage, targetTravelId, advance, endLocationChapter } = useFeatureTour();
  const isTourTarget = targetTravelId === item.id;
  const tourPointsHere = isTourTarget && stage === 'find-start';
  const tourWantsCrosshair = isTourTarget && stage === 'crosshair';

  const isHome = isHomeType(item.travel_type);
  const isHotel = isHotelType(item.travel_type);
  const isStartOfDay = isStartOfDayType(item.travel_type);
  const isEndOfDay = isEndOfDayType(item.travel_type);
  const isToggleable = isStartOfDay || isEndOfDay;

  const travelStyle = isStaleTravel
    ? 'bg-amber-400/5 border-amber-400/30 opacity-60'
    : isHome
      ? 'bg-green-600/10 border-green-600/30 text-green-400'
      : isHotel
        ? 'bg-blue-500/5 border-blue-500/30 text-blue-300'
        : 'bg-gray-500/10 border-gray-500/30';
  const travelIcon = isHome ? '\u{1F3E0}'
    : isHotel ? '\u{1F3E8}'
    : '\u{1F697}';

  const handleSaveHours = async () => {
    const newHours = parseFloat(editHours);
    if (isNaN(newHours) || newHours < 0) { setEditing(false); return; }
    const { error } = await supabase.from('route_items').update({ travel_hours: newHours }).eq('id', item.id);
    if (error) { console.error('Failed to save travel hours:', error); }
    setEditing(false);
  };

  const handleToggle = (side) => {
    if (!onTravelToggle) return;
    if (side === 'home' && isHome) return;
    if (side === 'hotel' && isHotel) return;
    const newType = side === 'home'
      ? (isStartOfDay ? 'start_of_day_home' : 'end_of_day_home')
      : (isStartOfDay ? 'start_of_day_hotel' : 'end_of_day_hotel');
    onTravelToggle(item, newType);
  };

  const handleStartEditOrigin = () => {
    // Pre-fill: use existing override, or home base only for home-type rows
    setEditOrigin(item.travel_origin_override || (isHome ? (homeBase || '') : ''));
    setEditingOrigin(true);
    // Opening the row the tour pointed at hands off to the crosshair button.
    if (tourPointsHere) advance('crosshair');
  };

  const handleSaveOrigin = async () => {
    const addr = editOrigin.trim();
    if (!addr) { setEditingOrigin(false); return; }
    await supabase.from('route_items')
      .update({ travel_origin_override: addr })
      .eq('id', item.id);
    setEditingOrigin(false);
    // Saving an address always switches to Home (with that address)
    const type = isStartOfDay ? 'start_of_day_home' : 'end_of_day_home';
    if (onTravelToggle) onTravelToggle(item, type, addr);
  };

  return (
    <div className={`rounded px-3 py-1 text-xs border ${travelStyle} ${
      tourPointsHere ? 'ring-2 ring-green-400/70' : ''
    }`}>
      <div className="flex items-center gap-2">
        <span>{travelIcon}</span>

        {/* Pill-style toggle for start/end of day */}
        {isToggleable && (
          <span className="inline-flex rounded-full overflow-hidden border border-gray-500/40 shrink-0">
            <button
              onClick={() => handleToggle('home')}
              className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                isHome
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-400 hover:text-gray-300'
              }`}
            >
              Home
            </button>
            <button
              onClick={() => handleToggle('hotel')}
              className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                isHotel
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-700 text-gray-400 hover:text-gray-300'
              }`}
            >
              Hotel
            </button>
          </span>
        )}

        {/* Description — inline-editable address for all start/end of day rows */}
        {isToggleable && !editingOrigin ? (
          <button
            onClick={handleStartEditOrigin}
            className={`flex-1 text-left transition-colors ${
              tourPointsHere
                // Blink the address itself, so the tour points at the thing to click.
                ? 'animate-tour-blink text-white bg-green-600/20 ring-1 ring-green-400/70 rounded px-1.5'
                : 'text-gray-400 hover:text-white'
            }`}
            title={isStartOfDay ? 'Click to edit start location' : 'Click to edit destination'}
          >
            {item.travel_description}
          </button>
        ) : isToggleable && editingOrigin ? (
          <span className="flex-1 flex items-center gap-1">
            <span className="relative flex-1 flex items-center">
              {/* Overlaid inside the field's left edge — appears as soon as the
                  row is clicked into edit mode. */}
              <span className="absolute left-1 z-10">
                <CurrentLocationButton
                  compact
                  bounce={tourWantsCrosshair}
                  onResolved={(addr) => {
                    setEditOrigin(addr);
                    // Using it finishes the location chapter and reveals the Map button.
                    if (tourWantsCrosshair) endLocationChapter();
                  }}
                />
              </span>
              <input
                type="text"
                value={editOrigin}
                onChange={(e) => setEditOrigin(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveOrigin(); if (e.key === 'Escape') setEditingOrigin(false); }}
                autoFocus
                placeholder={isHome ? (homeBase || 'Enter address...') : (isStartOfDay ? 'Enter start address...' : 'Enter destination...')}
                className="flex-1 w-full pl-7 pr-1.5 py-0.5 bg-gray-900 border border-gray-500 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-green-600"
              />
            </span>
            <button onClick={handleSaveOrigin} className="text-green-400 hover:text-green-400/80">&#10003;</button>
            <button onClick={() => setEditingOrigin(false)} className="text-gray-400 hover:text-white">&#10005;</button>
          </span>
        ) : (
          <span className="flex-1 text-gray-400">{item.travel_description}</span>
        )}

        {/* Hours — inline editable */}
        {editing ? (
          <span className="flex items-center gap-1">
            <input
              type="number"
              step="0.05"
              value={editHours}
              onChange={(e) => setEditHours(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveHours(); if (e.key === 'Escape') setEditing(false); }}
              autoFocus
              className="w-14 px-1 py-0.5 bg-gray-900 border border-gray-500 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-green-600"
            />
            <span className="text-gray-400">h</span>
            <button onClick={handleSaveHours} className="text-green-400 hover:text-green-400/80">&#10003;</button>
            <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-white">&#10005;</button>
          </span>
        ) : (
          <button
            onClick={() => { setEditHours(item.travel_hours || 0); setEditing(true); }}
            className="text-gray-400 hover:text-white cursor-pointer"
            title="Click to edit travel time"
          >
            {item.travel_hours != null ? `${item.travel_hours}h` : '—'}
          </button>
        )}
        {item.travel_distance_km && <span className="text-gray-400">{item.travel_distance_km}km</span>}
        {isStaleTravel && <span className="text-amber-400 font-medium">STALE</span>}
      </div>
    </div>
  );
};

// ─── Transit Row (user-added travel segment, draggable, deletable) ──

const TransitRow = ({ item, dragListeners, isStaleTravel, onEditTransit, onDeleteItem }) => {
  const isLocked = !!item.travel_hours_locked;
  const rowStyle = isStaleTravel
    ? 'bg-amber-400/5 border-amber-400/30 opacity-60'
    : 'bg-gray-500/15 border-gray-500/40';

  return (
    <div className={`rounded px-3 py-1 text-xs border ${rowStyle}`}>
      <div className="flex items-center gap-2">
        {dragListeners ? (
          <button
            {...dragListeners}
            className="cursor-grab text-gray-400 hover:text-gray-300 touch-none"
            aria-label="Drag to reorder"
          >
            &#9776;
          </button>
        ) : (
          <span className="text-gray-400">{'\u{1F9ED}'}</span>
        )}
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-500/30 text-gray-300 font-medium uppercase tracking-wide">{item.category === 'other' ? 'Other' : 'Travel'}</span>
        <button
          onClick={() => onEditTransit && onEditTransit(item)}
          className="flex-1 text-gray-300 text-left hover:text-white transition-colors truncate"
          title="Click to edit this travel segment"
        >
          {item.travel_description || (item.travel_destination ? `→ ${item.travel_destination}` : 'Travel segment')}
        </button>
        <span className="text-gray-300">
          {item.travel_hours != null ? `${parseFloat(item.travel_hours).toFixed(2)}h` : '—'}
        </span>
        {item.travel_distance_km != null && !isLocked && (
          <span className="text-gray-400">{item.travel_distance_km}km</span>
        )}
        {isLocked && (
          <span className="text-[10px] text-amber-300 font-medium" title="Hours locked — recalc preserves them">
            LOCKED
          </span>
        )}
        {isStaleTravel && <span className="text-amber-400 font-medium">STALE</span>}
        <button
          onClick={() => onEditTransit && onEditTransit(item)}
          className="text-gray-400 hover:text-white transition-colors"
          title="Edit travel"
          aria-label="Edit travel"
        >
          &#9998;
        </button>
        {onDeleteItem && (
          <button
            onClick={() => onDeleteItem(item)}
            className="text-red-300 hover:text-red-200 hover:bg-red-500/20 text-lg leading-none px-1.5 rounded transition-colors"
            title="Remove this travel segment"
            aria-label="Remove travel"
          >
            &times;
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Item Card ────────────────────────────────────────────

const ItemCard = ({ item, route, dragListeners, onTogglePin, onEditJob, onEditTransit, onDeleteItem, isStaleTravel, onTravelToggle, homeBase }) => {
  const [expanded, setExpanded] = useState(false);
  const [showPinForm, setShowPinForm] = useState(false);
  // Called before the row-type early returns — hooks cannot be conditional.
  // Passing null for non-JOB rows makes the hint a no-op for them.
  const { hintProps, isHinted } = useHintHover(item.row_type === 'JOB' ? item.id : null);

  if (item.row_type === 'TRAVEL') {
    return <TravelRow item={item} isStaleTravel={isStaleTravel} onTravelToggle={onTravelToggle} homeBase={homeBase} />;
  }

  if (item.row_type === 'TRANSIT') {
    return (
      <TransitRow
        item={item}
        dragListeners={dragListeners}
        isStaleTravel={isStaleTravel}
        onEditTransit={onEditTransit}
        onDeleteItem={onDeleteItem}
      />
    );
  }

  if (item.row_type === 'BREAK') {
    return (
      <div className="border border-dashed border-gray-500 rounded-md px-3 py-2 text-sm text-gray-400">
        <span className="mr-2">&#9208;</span> BREAK
      </div>
    );
  }

  // JOB card
  const isPinned = item.is_pinned;

  const handlePinSave = (pinTime, pinNotes) => {
    onTogglePin(item, true, pinTime, pinNotes);
    setShowPinForm(false);
  };

  const handleUnpin = () => {
    onTogglePin(item, false, null, null);
  };

  // One guard rather than stopPropagation on every control: a click that began
  // inside an interactive child belongs to that child, not to the card.
  const handleCardClick = (e) => {
    if (e.target.closest('button, a, input, select, textarea, label')) return;
    setExpanded((v) => !v);
  };

  return (
    <div
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((v) => !v); }
      }}
      {...hintProps}
      className={`group rounded-md px-3 py-2 text-sm cursor-pointer transition-colors ${
        isHinted ? 'animate-tour-nudge' : ''
      } ${
        isPinned
          ? `bg-gray-700 hover:bg-gray-700/70 border-l-4 ${item.pin_needs_review ? 'border-amber-400 ring-1 ring-amber-400/30' : 'border-orange-400'}`
          : 'bg-gray-700 hover:bg-gray-700/70 border border-gray-500/50 hover:border-green-600/60'
      }`}
    >
      {/* Top row: drag handle, name, value/hours, actions */}
      <div className="flex items-center gap-2">
        {dragListeners ? (
          <button
            {...dragListeners}
            className="cursor-grab text-gray-400 hover:text-gray-300 touch-none"
            aria-label="Drag to reorder"
          >
            &#9776;
          </button>
        ) : isPinned ? (
          <span className="flex items-center gap-1">
            <span className={item.pin_needs_review ? 'text-amber-400' : 'text-orange-400'} title={`Pinned: ${item.pin_notes || 'Scheduled arrival'}`}>
              &#128204;
            </span>
            {item.pin_needs_review && (
              <span className="text-xs px-1.5 py-0.5 bg-amber-400/20 text-amber-300 rounded font-medium">Review</span>
            )}
          </span>
        ) : null}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-white truncate">{item.company || 'Unnamed Job'}</span>
            <StatusBadge status={item.booking_status} hideBooked />
          </div>
          <div className="text-gray-400 text-xs truncate mt-0.5 hidden sm:block">{item.location}</div>
          {!expanded && (
            <div className="hidden sm:flex flex-wrap gap-x-3 text-xs text-gray-400 mt-0.5">
              {item.contact_name && <span>{item.contact_name}</span>}
              {item.phone && <span>{item.phone}</span>}
              {item.site_description && <span className="truncate max-w-[200px]">{item.site_description}</span>}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Link buttons — hidden on mobile, shown on expand */}
          <div className="hidden sm:flex items-center gap-1">
            {item.quote_link && (
              <a href={item.quote_link} target="_blank" rel="noopener noreferrer"
                className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30">Quote</a>
            )}
            {item.oprec_link && (
              <a href={item.oprec_link} target="_blank" rel="noopener noreferrer"
                className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30">Prev OPREC</a>
            )}
            {item.kmz_link && (
              <a href={item.kmz_link} target="_blank" rel="noopener noreferrer"
                className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30">KMZ</a>
            )}
            {item.image_link && (
              <a href={item.image_link} target="_blank" rel="noopener noreferrer"
                className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30">Image</a>
            )}
          </div>

          {item.value && (
            <span className="text-green-400 font-medium text-xs sm:text-sm">${parseFloat(item.value).toLocaleString()}</span>
          )}
          <span className="text-gray-300 text-xs">
            {item.estimated_hours}h
          </span>
        </div>

        {isPinned && item.pin_time && (
          <span className="text-xs text-orange-300 font-medium hidden sm:inline">{item.pin_time}</span>
        )}

        {/* Pin/Unpin button */}
        <button
          onClick={() => {
            if (isPinned && item.pin_needs_review) {
              setShowPinForm(true);
            } else if (isPinned) {
              handleUnpin();
            } else {
              setShowPinForm(!showPinForm);
            }
          }}
          className={`hidden sm:inline text-xs px-1.5 py-0.5 rounded transition-colors ${
            isPinned && item.pin_needs_review
              ? 'text-amber-300 hover:text-amber-400 hover:bg-amber-400/10'
              : isPinned
                ? 'text-orange-300 hover:text-orange-400 hover:bg-orange-400/10'
                : 'text-gray-400 hover:text-orange-300 hover:bg-orange-400/10'
          }`}
          title={isPinned && item.pin_needs_review ? 'Review pin timing' : isPinned ? 'Unpin this job' : 'Pin this job'}
        >
          {isPinned && item.pin_needs_review ? 'Review' : isPinned ? 'Unpin' : 'Pin'}
        </button>

        {/* Edit button */}
        <button
          onClick={() => onEditJob(item)}
          className="text-xs text-gray-400 hover:text-white transition-colors"
          title="Edit job"
        >
          &#9998;
        </button>

        {/* Affordance only \u2014 the whole card is the click target, so this must
            not be a button (it would swallow the click via the guard above). */}
        <span
          className={`text-gray-400 text-base leading-none px-2 py-1 ml-1 transition-opacity ${
            expanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
          aria-hidden
        >
          {expanded ? '\u25B2' : '\u25BC'}
        </span>

        {/* Remove from route (red X) */}
        {onDeleteItem && (
          <button
            onClick={() => onDeleteItem(item)}
            className="text-red-300 hover:text-red-200 hover:bg-red-500/20 text-lg leading-none px-1.5 py-0.5 rounded transition-colors ml-1"
            title="Remove from route"
            aria-label="Remove job from route"
          >
            &times;
          </button>
        )}
      </div>

      {/* Pin form (shown when clicking Pin on an unpinned job, or Review on a flagged pinned job) */}
      {showPinForm && (!isPinned || item.pin_needs_review) && (
        <PinForm item={item} onSave={handlePinSave} onCancel={() => setShowPinForm(false)} saveLabel={item.pin_needs_review ? 'Confirm Pin' : 'Pin Job'} />
      )}

      {expanded && (
        <div className="mt-2 pt-2 border-t border-gray-500/30 text-xs text-gray-300 space-y-2">
          {/* Mobile-only: location, links, pin controls */}
          <div className="sm:hidden space-y-1.5">
            {item.location && <div className="text-gray-400">{item.location}</div>}
            <div className="flex flex-wrap items-center gap-1.5">
              {item.quote_link && (
                <a href={item.quote_link} target="_blank" rel="noopener noreferrer"
                  className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30">Quote</a>
              )}
              {item.oprec_link && (
                <a href={item.oprec_link} target="_blank" rel="noopener noreferrer"
                  className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30">Prev OPREC</a>
              )}
              {item.kmz_link && (
                <a href={item.kmz_link} target="_blank" rel="noopener noreferrer"
                  className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30">KMZ</a>
              )}
              {item.image_link && (
                <a href={item.image_link} target="_blank" rel="noopener noreferrer"
                  className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30">Image</a>
              )}
              <button
                onClick={() => {
                  if (isPinned) handleUnpin();
                  else setShowPinForm(!showPinForm);
                }}
                className={`px-1.5 py-0.5 rounded transition-colors ${
                  isPinned ? 'text-orange-300 hover:bg-orange-400/10' : 'text-gray-400 hover:text-orange-300 hover:bg-orange-400/10'
                }`}
              >
                {isPinned ? 'Unpin' : 'Pin'}
              </button>
              {isPinned && item.pin_time && <span className="text-orange-300 font-medium">{item.pin_time}</span>}
            </div>
          </div>
          {/* Contact & Site Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {item.work_type && <div><span className="text-gray-400">Type:</span> {item.work_type}</div>}
            {item.contact_name && <div><span className="text-gray-400">Contact:</span> {item.contact_name}</div>}
            {item.phone && <div><span className="text-gray-400">Phone:</span> {item.phone}</div>}
            {item.email && <div><span className="text-gray-400">Email:</span> <a href={`mailto:${item.email}`} className="text-blue-300 hover:underline">{item.email}</a></div>}
            {item.area_acres && <div><span className="text-gray-400">Area:</span> {item.area_acres} acres</div>}
            {item.completed_by && <div><span className="text-gray-400">Crew:</span> {item.completed_by}</div>}
            {(item.last_completed || item.last_completed_year) && (
              <div><span className="text-gray-400">Last done:</span> {item.last_completed_year || item.last_completed}</div>
            )}
            {item.invoice_number && <div><span className="text-gray-400">Invoice:</span> {item.invoice_number}</div>}
          </div>

          {/* Notes */}
          {item.site_description && (
            <div><span className="text-gray-400">Site:</span> {item.site_description}</div>
          )}
          {item.client_notes && (
            <div className="text-amber-300"><span className="text-gray-400">Client notes:</span> {item.client_notes}</div>
          )}
          {item.internal_notes && (
            <div><span className="text-gray-400">Internal notes:</span> {item.internal_notes}</div>
          )}
          {item.pin_notes && (
            <div className="text-orange-300"><span className="text-gray-400">Pin reason:</span> {item.pin_notes}</div>
          )}

          {/* Chemicals — route_items values take priority, fall back to CRM reference */}
          {(() => {
            const hasRouteChem = item.chem_ru_l > 0 || item.chem_esp_ml > 0 || item.chem_navius_g > 0 || item.chem_telar_g > 0 ||
              item.chem_24d_l > 0 || item.chem_clearview_g > 0 || item.chem_overdrive_g > 0 || item.chem_trillion_l > 0;
            if (hasRouteChem) {
              return (
                <div>
                  <span className="text-gray-400">Chemicals:</span>{' '}
                  {[
                    item.chem_ru_l > 0 && `RU ${item.chem_ru_l}L`,
                    item.chem_esp_ml > 0 && `ESP ${item.chem_esp_ml}mL`,
                    item.chem_navius_g > 0 && `Navius ${item.chem_navius_g}g`,
                    item.chem_telar_g > 0 && `Telar ${item.chem_telar_g}g`,
                    item.chem_24d_l > 0 && `2,4-D ${item.chem_24d_l}L`,
                    item.chem_clearview_g > 0 && `Clearview ${item.chem_clearview_g}g`,
                    item.chem_overdrive_g > 0 && `Overdrive ${item.chem_overdrive_g}g`,
                    item.chem_trillion_l > 0 && `Trillion ${item.chem_trillion_l}L`,
                  ].filter(Boolean).join(' | ')}
                </div>
              );
            }
            if (item.chemicals_summary) {
              return (
                <div>
                  <span className="text-gray-400">Est. Chem usage:</span>{' '}
                  <span className="text-gray-400">{item.chemicals_summary.replace(/\|/g, ' | ').replace(/:/g, ' ')}</span>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
    </div>
  );
};

// ─── Droppable Day Zone (makes empty days valid drop targets) ─────

const DroppableDayZone = ({ dayNumber, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `day-drop-${dayNumber}`,
    data: { dayNumber },
  });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[60px] rounded-md transition-colors ${isOver ? 'bg-green-600/10 ring-2 ring-green-600/30' : ''}`}
    >
      {children}
    </div>
  );
};

// ─── Draggable Day Header (for day-level reorder) ────────

const DraggableDayHeader = ({ dayNumber, hasPinnedItems }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `day-swap-${dayNumber}`,
    disabled: hasPinnedItems,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <span
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(hasPinnedItems ? {} : listeners)}
      className={`${hasPinnedItems ? 'text-gray-700 cursor-not-allowed' : 'text-gray-400 hover:text-gray-300 cursor-grab'} touch-none`}
      title={hasPinnedItems ? 'Unpin all jobs to drag this day' : 'Drag to swap with another day'}
      aria-label="Drag to reorder day"
    >
      &#9776;
    </span>
  );
};

// ─── Day Column ───────────────────────────────────────────

const DayColumn = ({ dayNumber, items, route, isStaleTravel, onTogglePin, onEditJob, onEditTransit, onDeleteItem, onAddJob, onAddTransit, onCancelDay, onMoveDay, onOptimizeDay, onSetDayExtension, holidays, onTravelToggle, homeBase, entry, onOpenClock, hoursEnabled, onOpenNotes }) => {
  const hours = getDayHours(items);
  const dayOverride = items.find((i) => i.day_override_hours)?.day_override_hours;
  const limit = dayOverride ? parseFloat(dayOverride) : 8;
  const isExtended = !!dayOverride;
  const hourColor = getHourColor(hours, limit);
  const weekend = isWeekend(route.start_date, dayNumber);
  const [showExtensionForm, setShowExtensionForm] = useState(false);
  const [travelMenuOpen, setTravelMenuOpen] = useState(false);
  const [mapState, setMapState] = useState(null); // null = closed, { intro: bool } = open
  const { mapButtonVisible, mapButtonBlinks, complete: completeTour } = useFeatureTour();

  // Check if this day is a statutory holiday
  const calDate = route.start_date ? getCalendarDate(route.start_date, dayNumber) : null;
  const dateStr = calDate ? calDate.toISOString().split('T')[0] : null;
  const holiday = (holidays || []).find((h) => h.holiday_date === dateStr);
  const firstJobLocation = items.find((i) => i.row_type === 'JOB' && i.location)?.location || null;

  // ─── Actual-hours / clock state for this day ──────────────
  const actualHours = entryActualHours(entry);
  const isOpenEntry = !!(entry && entry.clock_in_at && !entry.clock_out_at);
  const canClock = hoursEnabled && !!dateStr;
  const clockMode = !entry || !entry.clock_in_at ? 'in' : !entry.clock_out_at ? 'out' : 'edit';
  // Start/end physical locations for time-zone prefill — reuse the day's travel endpoints,
  // falling back to first/last job or home base when a bare "Hotel" can't be geocoded.
  const startTravel = items.find((i) => i.row_type === 'TRAVEL' && isStartOfDayType(i.travel_type));
  const endTravel = [...items].reverse().find((i) => i.row_type === 'TRAVEL' && isEndOfDayType(i.travel_type));
  const lastJobLocation = [...items].reverse().find((i) => i.row_type === 'JOB' && i.location)?.location || null;
  const startLocation = startTravel?.travel_origin_override
    || (isHomeType(startTravel?.travel_type) ? homeBase : firstJobLocation)
    || homeBase || firstJobLocation;
  const endLocation = endTravel?.travel_origin_override
    || (isHomeType(endTravel?.travel_type) ? homeBase : lastJobLocation)
    || homeBase || lastJobLocation;

  // ─── Driving route for this day (Google / Apple Maps hand-off) ────
  // Stops are the day's real addresses in route order: the start-of-day
  // endpoint, each job/transit destination, then the end-of-day endpoint.
  // A hotel row with no saved address is a placeholder, not a location, so it
  // is skipped and reported rather than being sent to Maps as "Hotel".
  const { mapStops, mapExcluded } = useMemo(() => {
    const stops = [];
    const excluded = [];
    const add = (label, address, kind) => {
      const addr = (address || '').trim();
      if (!isMappableAddress(addr)) {
        excluded.push({
          label,
          reason: addr ? `"${addr}" is not a routable address` : 'no address on file',
        });
        return;
      }
      // Collapse a repeat of the previous stop (e.g. ending where you started).
      const prev = stops[stops.length - 1];
      if (prev && prev.address.toLowerCase() === addr.toLowerCase()) return;
      stops.push({ label, address: addr, kind });
    };

    const endpointAddress = (travel) =>
      travel?.travel_origin_override || (isHomeType(travel?.travel_type) ? homeBase : null);

    if (startTravel) add('Start of day', endpointAddress(startTravel), 'start');
    for (const it of items) {
      if (it.row_type === 'JOB') {
        add(it.company || 'Job', it.location, 'job');
      } else if (it.row_type === 'TRANSIT' && it.travel_destination) {
        add(it.travel_description || 'Transit', it.travel_destination, 'transit');
      }
    }
    if (endTravel) add('End of day', endpointAddress(endTravel), 'end');

    return { mapStops: stops, mapExcluded: excluded };
  }, [items, startTravel, endTravel, homeBase]);

  const itemIds = items.map((i) => i.id);

  const handleExtensionSave = (newLimit) => {
    onSetDayExtension(dayNumber, items, newLimit);
    setShowExtensionForm(false);
  };

  const handleClearExtension = () => {
    onSetDayExtension(dayNumber, items, null);
    setShowExtensionForm(false);
  };

  return (
    <div className="bg-gray-700/50 rounded-lg p-4">
      {/* Day header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <DraggableDayHeader dayNumber={dayNumber} hasPinnedItems={items.some(i => i.is_pinned)} />
          <h3 className="text-white font-semibold">Day {dayNumber}</h3>
          <span className={`text-sm ${holiday ? 'text-red-200' : weekend ? 'text-amber-400' : 'text-gray-400'}`}>
            {formatDate(route.start_date, dayNumber)}
            {weekend && ' (weekend)'}
          </span>
          {actualHours != null ? (
            <span className="flex items-center gap-1.5">
              <span
                className={`text-sm font-medium ${getHourColor(actualHours, limit)}`}
                title={`Actual worked time — in ${formatInOriginZone(entry.clock_in_at, entry.clock_in_zone)}, out ${formatInOriginZone(entry.clock_out_at, entry.clock_out_zone)}`}
              >
                {actualHours.toFixed(1)}h / {limit.toFixed(1)}h
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-600/20 text-green-400 font-medium uppercase tracking-wide">actual</span>
              {entry.crossed_timezones && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-medium uppercase tracking-wide" title="This day crossed a time-zone line">crossed&nbsp;tz</span>
              )}
              {isExtended && <span className="text-amber-400 text-xs">EXTENDED</span>}
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <span
                className={`text-sm font-medium ${hourColor}`}
                title="Approximate scheduled hours. Assuming an 8h day is ~10h with breaks/stops/unforeseen."
              >
                {hours.toFixed(1)}h / {limit.toFixed(1)}h
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-400 font-medium uppercase tracking-wide">{isOpenEntry ? 'open' : 'estimated'}</span>
              {isExtended && <span className="text-amber-400 text-xs">EXTENDED</span>}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Clock in / out (actual payroll hours) */}
          {canClock && (
            <button
              onClick={() => onOpenClock(dayNumber, clockMode, { workDate: dateStr, startLocation, endLocation })}
              className={`text-[11px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded transition-colors ${
                clockMode === 'in' ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                : clockMode === 'out' ? 'bg-amber-400/20 text-amber-300 hover:bg-amber-400/30'
                : 'text-gray-400 hover:text-white hover:bg-gray-500/20'
              }`}
              title={clockMode === 'in' ? 'Set the start time for this day' : clockMode === 'out' ? 'Set the stop time — end this day' : 'Edit start/stop times'}
            >
              {clockMode === 'in' ? 'Start Time' : clockMode === 'out' ? 'Stop Time' : 'Edit Times'}
            </button>
          )}
          {/* General notes for the day (flows into the time export) */}
          {canClock && (
            <button
              onClick={() => onOpenNotes(dayNumber, { workDate: dateStr })}
              className={`text-[11px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded transition-colors ${
                entry?.notes ? 'bg-green-600/10 text-green-400 hover:bg-green-600/20' : 'text-gray-400 hover:text-white hover:bg-gray-500/20'
              }`}
              title="Add a general note for this day (shows in the time export)"
            >
              Notes{entry?.notes ? ' ✓' : ''}
            </button>
          )}
          {/* Day extension toggle */}
          <button
            onClick={() => setShowExtensionForm(!showExtensionForm)}
            className={`text-[11px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded transition-colors ${
              isExtended
                ? 'bg-amber-400/20 text-amber-300 hover:bg-amber-400/30'
                : 'text-gray-400 hover:text-amber-300 hover:bg-amber-400/10'
            }`}
            title={isExtended ? 'Modify day extension' : 'Extend this day'}
          >
            {isExtended ? 'Extended' : 'Extend'}
          </button>

          <button
            onClick={() => onCancelDay(dayNumber)}
            className="text-[11px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 text-gray-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
            title="Cancel this day and push jobs forward"
          >
            Cancel
          </button>

          <button
            onClick={() => onOptimizeDay(dayNumber)}
            className="text-[11px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 text-gray-400 hover:text-green-400 hover:bg-green-600/10 rounded transition-colors"
            title="Optimize job order to minimize driving"
          >
            Optimize
          </button>

          {mapStops.length > 0 && mapButtonVisible && (
            <button
              onClick={() => {
                setMapState({ intro: mapButtonBlinks });
                // Opening it from the tour finishes onboarding for good.
                if (mapButtonBlinks) completeTour();
              }}
              className={`text-[11px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded transition-colors ${
                mapButtonBlinks
                  ? 'animate-tour-nudge text-white bg-blue-500/25 ring-1 ring-blue-300/70'
                  : 'text-gray-400 hover:text-blue-300 hover:bg-blue-500/10'
              }`}
              title="Driving directions for this day, in route order — opens in Google or Apple Maps, or scans to a phone"
            >
              🗺 Map
            </button>
          )}

          <button
            onClick={() => onMoveDay(dayNumber)}
            className="text-[11px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 text-gray-400 hover:text-blue-300 hover:bg-blue-500/10 rounded transition-colors hidden sm:inline"
            title="Move all jobs to a different route"
          >
            Move
          </button>

          <button
            onClick={() => onAddJob(dayNumber)}
            className="text-[11px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 text-gray-400 hover:text-green-400 hover:bg-green-600/10 rounded transition-colors"
            title="Add a job to this day"
          >
            + Job
          </button>

          <div className="relative">
            <button
              onClick={() => setTravelMenuOpen((v) => !v)}
              className="text-[11px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 text-gray-400 hover:text-gray-300 hover:bg-gray-500/20 rounded transition-colors"
              title="Add a non-billable segment (driving Transit, or Other: lunch/standby/repositioning)"
            >
              + Add ▾
            </button>
            {travelMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setTravelMenuOpen(false)} />
                <div className="absolute right-0 mt-1 z-20 w-48 bg-gray-800 border border-gray-500/40 rounded-md shadow-xl py-1">
                  <button
                    onClick={() => { setTravelMenuOpen(false); onAddTransit(dayNumber, 'transit'); }}
                    className="block w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 hover:text-white"
                  >
                    Transit <span className="text-gray-400">— driving (Maps)</span>
                  </button>
                  <button
                    onClick={() => { setTravelMenuOpen(false); onAddTransit(dayNumber, 'other'); }}
                    className="block w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 hover:text-white"
                  >
                    Other <span className="text-gray-400">— duration + note</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {dateStr && <WeatherStrip dateISO={dateStr} address={firstJobLocation} />}

      {/* Statutory holiday banner */}
      {holiday && (
        <div className="mb-3 px-3 py-1.5 bg-red-500/15 border border-red-500/30 rounded text-xs text-red-200 font-medium flex items-center gap-2">
          <span>&#127881;</span> {holiday.name} — Statutory Holiday (No work scheduled)
        </div>
      )}

      {/* Driving route for this day (Google / Apple Maps) */}
      {mapState && (
        <DayRouteMapModal
          dayNumber={dayNumber}
          dateLabel={formatDate(route.start_date, dayNumber)}
          stops={mapStops}
          excluded={mapExcluded}
          showIntro={mapState.intro}
          onClose={() => setMapState(null)}
        />
      )}

      {/* Day extension form */}
      {showExtensionForm && (
        <div className="mb-3">
          <DayExtensionForm
            currentOverride={dayOverride}
            onSave={handleExtensionSave}
            onCancel={() => setShowExtensionForm(false)}
          />
          {isExtended && (
            <button
              onClick={handleClearExtension}
              className="text-xs text-red-300 hover:text-red-200 mt-1"
            >
              Remove extension (reset to 8h)
            </button>
          )}
        </div>
      )}



      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <DroppableDayZone dayNumber={dayNumber}>
          <div className="space-y-2">
            {items.map((item) => (
              <SortableItemCard
                key={item.id}
                item={item}
                route={route}
                onTogglePin={onTogglePin}
                onEditJob={onEditJob}
                onEditTransit={onEditTransit}
                onDeleteItem={onDeleteItem}
                isStaleTravel={isStaleTravel && (item.row_type === 'TRAVEL' || item.row_type === 'TRANSIT')}
                onTravelToggle={onTravelToggle}
                homeBase={homeBase}
              />
            ))}
          </div>
          {items.length === 0 && (
            <div className="text-center py-4 text-gray-400 text-sm border border-dashed border-gray-500/50 rounded-md">
              Drop jobs here or click + Job
            </div>
          )}
        </DroppableDayZone>
      </SortableContext>
    </div>
  );
};

// Flatten crm_job fields onto route_items so existing field access works.
// crm_job fields take priority over any stale legacy columns on route_items.
const flattenCrmJobs = (items) => (items || []).map(item => {
  if (item.crm_job) {
    const { crm_job, ...rest } = item;
    return { ...rest, ...crm_job, id: rest.id, crm_job_id: rest.crm_job_id, crm_job };
  }
  return item;
});

// ─── Route Detail Page ────────────────────────────────────

// Module-level cache so revisits render last-seen state instantly while the
// background refetch refreshes data. Realtime subscription keeps it in sync.
const routeCache = new Map(); // routeId -> { route, items }

const RouteDetailPage = () => {
  const { routeId } = useParams();
  const cached = routeCache.get(routeId);
  const [route, setRoute] = useState(cached?.route || null);
  const [items, setItems] = useState(cached?.items || []);
  const [loading, setLoading] = useState(!cached);

  // Feature tour: publish the first start-of-day row so the tour can point at
  // it. Items arrive ordered by day_number then sort_order, so the first match
  // is the earliest one on the route.
  const { setTargetTravelId } = useFeatureTour();
  const firstStartTravelId = useMemo(() => (
    items.find((i) => i.row_type === 'TRAVEL' && isStartOfDayType(i.travel_type))?.id || null
  ), [items]);
  useEffect(() => {
    setTargetTravelId(firstStartTravelId);
  }, [firstStartTravelId, setTargetTravelId]);

  // Modal / panel state
  const [jobFormState, setJobFormState] = useState(null); // { dayNumber, sortOrder, existingItem? }
  const [transitFormState, setTransitFormState] = useState(null); // { dayNumber, sortOrder, priorEndpoint, existingItem? }
  const [cancelDayState, setCancelDayState] = useState(null); // { dayNumber, jobCount, pinnedCount }
  const [deleteItemState, setDeleteItemState] = useState(null); // { id, company }
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [moveDayState, setMoveDayState] = useState(null); // { dayNumber, jobCount }
  const [showEditRoute, setShowEditRoute] = useState(false);
  const [showPushRoute, setShowPushRoute] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeStrategy, setOptimizeStrategy] = useState('farthest_first');

  // Applicators & holidays
  const [applicators, setApplicators] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]); // actual-hours layer for this route's applicator
  const [clockState, setClockState] = useState(null); // { dayNumber, mode, workDate, startLocation, endLocation, existingEntry }
  const [notesState, setNotesState] = useState(null); // { dayNumber, workDate, existingEntry }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Track stale travel rows (after drag reorder, travel times may be wrong)
  const [staleTravelDays, setStaleTravelDays] = useState(new Set());
  const [recalculating, setRecalculating] = useState(false);

  // Sweep auto-generated TRAVEL rows from any day that no longer has a JOB or TRANSIT.
  // Catches every removal path (delete, drag-out, cancel-day cascade, move-day-to-route)
  // and scrubs pre-existing orphans on load. Idempotent: a clean items array is a no-op.
  useEffect(() => {
    if (!items.length) return;
    const byDay = new Map();
    for (const it of items) {
      if (it.day_number == null) continue;
      const bucket = byDay.get(it.day_number) || { hasJobOrTransit: false, travelIds: [] };
      if (it.row_type === 'JOB' || it.row_type === 'TRANSIT') bucket.hasJobOrTransit = true;
      else if (it.row_type === 'TRAVEL') bucket.travelIds.push(it.id);
      byDay.set(it.day_number, bucket);
    }
    const orphanedIds = [];
    for (const { hasJobOrTransit, travelIds } of byDay.values()) {
      if (!hasJobOrTransit && travelIds.length) orphanedIds.push(...travelIds);
    }
    if (!orphanedIds.length) return;
    setItems(prev => prev.filter(i => !orphanedIds.includes(i.id)));
    supabase.from('route_items').delete().in('id', orphanedIds).then(({ error }) => {
      if (error) console.error('Failed to clean orphaned travel:', error);
    });
  }, [items]);

  // Drag overlay — shows card preview while dragging across weeks/days
  const [activeDragId, setActiveDragId] = useState(null);

  // Drag notification toast
  const [dragNotice, setDragNotice] = useState(null);

  // Travel calc progress
  const [travelProgress, setTravelProgress] = useState(null); // { current, total, description }

  const fetchData = useCallback(async () => {
    const [routeRes, itemsRes] = await Promise.all([
      supabase.from('routes').select('*').eq('id', routeId).single(),
      supabase
        .from('route_items')
        .select(`id, route_id, crm_job_id, row_type, day_number, sort_order,
          is_pinned, pin_time, pin_notes, pin_needs_review, day_override_hours, completed_by,
          oprec_link, quote_link, kmz_link,
          chem_ru_l, chem_esp_ml, chem_navius_g, chem_telar_g,
          chem_24d_l, chem_clearview_g, chem_overdrive_g, chem_trillion_l,
          travel_description, travel_hours, travel_distance_km, travel_type, travel_origin_override,
          travel_destination, travel_hours_locked, category,
          crm_job:crm_jobs(id, company, contact_name, email, phone, work_type,
            site_description, location, client_notes, internal_notes, value,
            estimated_hours, area_acres, last_completed_year, booking_status,
            sheet_row, oprec_link, quote_link, kmz_link, image_link, chemicals_summary,
            last_contact, last_completed, invoice_number)`)
        .eq('route_id', routeId)
        .order('day_number', { ascending: true })
        .order('sort_order', { ascending: true }),
    ]);

    if (routeRes.error) {
      console.error('Error fetching route:', routeRes.error);
    } else {
      setRoute(routeRes.data);
    }

    let flatItems = null;
    if (itemsRes.error) {
      console.error('Error fetching items:', itemsRes.error);
    } else {
      flatItems = flattenCrmJobs(itemsRes.data);
      setItems(flatItems);
    }

    if (!routeRes.error && flatItems) {
      routeCache.set(routeId, { route: routeRes.data, items: flatItems });
    }

    setLoading(false);
  }, [routeId]);

  // Reset state to the new route's cache (or empty) when routeId changes, then refetch.
  useEffect(() => {
    const next = routeCache.get(routeId);
    setRoute(next?.route || null);
    setItems(next?.items || []);
    setLoading(!next);
    fetchData();
  }, [routeId, fetchData]);

  // Fetch applicators and holidays once
  useEffect(() => {
    const fetchMeta = async () => {
      const [appRes, holRes] = await Promise.all([
        supabase.from('applicators').select('id, name, home_base_location').not('user_id', 'is', null).order('name'),
        supabase.from('statutory_holidays').select('*').order('holiday_date'),
      ]);
      if (appRes.data) setApplicators(appRes.data);
      if (holRes.data) setHolidays(holRes.data);
    };
    fetchMeta();
  }, []);

  // ─── Applicator assignment handler ────────────────
  const handleApplicatorChange = async (applicatorId) => {
    const newId = applicatorId || null;
    setRoute((prev) => ({ ...prev, applicator_id: newId }));
    await supabase.from('routes').update({ applicator_id: newId }).eq('id', routeId);
  };

  const handleEditRouteSaved = (updatedFields) => {
    setRoute((prev) => ({ ...prev, ...updatedFields }));
    setShowEditRoute(false);
  };

  // ─── Real-time subscriptions ────────────────────────
  // Debounced refetch prevents cascade storms during drag/travel/cancel operations

  useEffect(() => {
    let debounceTimer = null;
    const debouncedFetch = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fetchData(), 500);
    };

    const routeChannel = supabase
      .channel(`route-${routeId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'routes', filter: `id=eq.${routeId}` },
        (payload) => {
          if (payload.eventType === 'UPDATE') {
            setRoute(payload.new);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'route_items', filter: `route_id=eq.${routeId}` },
        debouncedFetch
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(routeChannel);
    };
  }, [routeId, fetchData]);

  // ─── Actual-hours layer (time_entries) ──────────────
  // Fetched for this route's applicator across the route's calendar span. Guarded so that
  // if migration 013 hasn't been applied yet, the page still works and shows "estimated".
  // Fetch a generous window from the route start (covers any multi-week route). Depending only
  // on applicator + start_date keeps this callback stable, so the realtime sub doesn't churn.
  const fetchTimeEntries = useCallback(async () => {
    const applicatorId = route?.applicator_id;
    const startDate = route?.start_date;
    if (!applicatorId || !startDate) { setTimeEntries([]); return; }
    try {
      const { data, error } = await supabase
        .from('time_entries')
        .select('*')
        .eq('employee_id', applicatorId)
        .gte('work_date', dayToISODate(startDate, 1))
        .lte('work_date', dayToISODate(startDate, 120));
      if (error) throw error;
      setTimeEntries(data || []);
    } catch (e) {
      setTimeEntries([]); // table missing / RLS — degrade to no actual entries
    }
  }, [route?.applicator_id, route?.start_date]);

  useEffect(() => { fetchTimeEntries(); }, [fetchTimeEntries]);

  // Realtime: refetch when this applicator's time_entries change.
  useEffect(() => {
    const applicatorId = route?.applicator_id;
    if (!applicatorId) return undefined;
    let t = null;
    const debounced = () => { clearTimeout(t); t = setTimeout(() => fetchTimeEntries(), 500); };
    let channel;
    try {
      channel = supabase
        .channel(`time-entries-${applicatorId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'time_entries', filter: `employee_id=eq.${applicatorId}` },
          debounced
        )
        .subscribe();
    } catch (e) { /* table missing — no realtime for actual hours yet */ }
    return () => { clearTimeout(t); if (channel) supabase.removeChannel(channel); };
  }, [route?.applicator_id, fetchTimeEntries]);

  // Map work_date → entry, and a resolver from a route day number.
  const entriesByDate = useMemo(() => {
    const m = new Map();
    for (const e of timeEntries) m.set(e.work_date, e);
    return m;
  }, [timeEntries]);
  const entryForDay = useCallback(
    (dayNumber) => (route?.start_date ? entriesByDate.get(dayToISODate(route.start_date, dayNumber)) : null) || null,
    [entriesByDate, route?.start_date]
  );

  const handleOpenClock = (dayNumber, mode, ctx) => {
    setClockState({ dayNumber, mode, ...ctx, existingEntry: entryForDay(dayNumber) });
  };

  const handleOpenNotes = (dayNumber, ctx) => {
    setNotesState({ dayNumber, ...ctx, existingEntry: entryForDay(dayNumber) });
  };

  // Only Grant tracks payroll hours — gate the clock + notes buttons to his routes.
  const hoursEnabled = route?.applicator_id === HOURS_EMPLOYEE_ID;

  // Group items by day
  const days = items.reduce((acc, item) => {
    const day = item.day_number || 0;
    if (!acc[day]) acc[day] = [];
    acc[day].push(item);
    return acc;
  }, {});

  const dayNumbers = Object.keys(days)
    .map(Number)
    .sort((a, b) => a - b);

  // ─── Pin/Unpin handler ──────────────────────────────

  const handleTogglePin = async (item, pinned, pinTime, pinNotes) => {
    // Optimistic update
    setItems((prev) =>
      prev.map((i) =>
        i.id === item.id
          ? { ...i, is_pinned: pinned, pin_time: pinTime, pin_notes: pinNotes, pin_needs_review: false }
          : i
      )
    );

    const { error } = await supabase
      .from('route_items')
      .update({ is_pinned: pinned, pin_time: pinTime, pin_notes: pinNotes, pin_needs_review: false })
      .eq('id', item.id);

    if (error) {
      console.error('Error toggling pin:', error);
      fetchData(); // revert
    }
  };

  // ─── Day Extension handler ──────────────────────────

  const handleSetDayExtension = async (dayNumber, dayItems, newLimit) => {
    // Store override on the first item of the day
    const targetItem = dayItems[0];
    if (!targetItem) return;

    // Optimistic update — set override on first item, clear from others
    setItems((prev) =>
      prev.map((i) => {
        if (i.day_number !== dayNumber) return i;
        if (i.id === targetItem.id) return { ...i, day_override_hours: newLimit };
        return { ...i, day_override_hours: null };
      })
    );

    // Clear override from all items on this day first
    for (const di of dayItems) {
      if (di.id !== targetItem.id && di.day_override_hours) {
        await supabase.from('route_items').update({ day_override_hours: null }).eq('id', di.id);
      }
    }

    // Set on the target item
    const { error } = await supabase
      .from('route_items')
      .update({ day_override_hours: newLimit })
      .eq('id', targetItem.id);

    if (error) {
      console.error('Error setting day extension:', error);
      fetchData();
    }
  };

  // ─── Cancel Day (weather cascade) ──────────────────

  const handleCancelDayStart = (dayNumber) => {
    const dayItems = days[dayNumber] || [];
    const nonPinned = dayItems.filter((i) => !i.is_pinned);
    const pinned = dayItems.filter((i) => i.is_pinned);
    setCancelDayState({
      dayNumber,
      jobCount: nonPinned.length,
      pinnedCount: pinned.length,
    });
  };

  const handleCancelDayConfirm = async () => {
    if (!cancelDayState) return;
    const { dayNumber } = cancelDayState;

    // Cascade logic:
    // 1. All non-pinned items on this day and subsequent days get day_number + 1
    // 2. Pinned items stay put
    // 3. If a pushed item collides with a pinned item's day, it goes to day after

    const updates = [];

    // Get all items sorted by day
    const sortedItems = [...items].sort((a, b) => a.day_number - b.day_number || a.sort_order - b.sort_order);

    // Collect pinned day numbers (these days are "occupied" by pinned items)
    const pinnedDays = new Set(
      sortedItems.filter((i) => i.is_pinned).map((i) => i.day_number)
    );

    for (const item of sortedItems) {
      if (item.day_number < dayNumber) continue; // days before cancelled day are unaffected
      if (item.is_pinned) continue; // pinned items never move

      let newDay = item.day_number + 1;

      // If the new day collides with a pinned item's day, bump past it
      while (pinnedDays.has(newDay) && newDay !== item.day_number) {
        newDay++;
      }

      if (newDay !== item.day_number) {
        updates.push({ id: item.id, day_number: newDay });
      }
    }

    // Optimistic update
    setItems((prev) => {
      const updateMap = new Map(updates.map((u) => [u.id, u.day_number]));
      return prev.map((i) => (updateMap.has(i.id) ? { ...i, day_number: updateMap.get(i.id) } : i));
    });

    setCancelDayState(null);

    // Write to Supabase
    for (const update of updates) {
      await supabase.from('route_items').update({ day_number: update.day_number }).eq('id', update.id);
    }
  };

  // Roll the route's start_date back by `offset` days so a pre-start day (e.g. the Sunday
  // ahead of a Monday start) becomes Day 1. Every item shifts +offset day_numbers so all
  // existing work keeps its calendar dates; the new leading day(s) come in empty.
  const handleIncludeLeadingDay = async (offset) => {
    if (!route?.start_date || !offset || offset < 1) return;
    const d = new Date(route.start_date + 'T00:00:00');
    d.setDate(d.getDate() - offset);
    const newStart = d.toISOString().split('T')[0];

    const updates = items
      .filter((i) => i.day_number != null)
      .map((i) => ({ id: i.id, day_number: i.day_number + offset }));

    // Optimistic local update (calendar dates preserved, day_numbers shifted forward)
    setItems((prev) => prev.map((i) => (i.day_number != null ? { ...i, day_number: i.day_number + offset } : i)));
    setRoute((prev) => ({ ...prev, start_date: newStart }));
    setStaleTravelDays((prev) => new Set([...prev].map((dn) => dn + offset)));

    // Persist
    await supabase.from('routes').update({ start_date: newStart }).eq('id', routeId);
    for (const u of updates) {
      await supabase.from('route_items').update({ day_number: u.day_number }).eq('id', u.id);
    }
  };

  // ─── Add/Edit job handlers ─────────────────────────

  const handleAddJob = (dayNumber) => {
    const dayItems = days[dayNumber] || [];
    const maxOrder = dayItems.reduce((max, i) => Math.max(max, i.sort_order || 0), 0);
    setJobFormState({ dayNumber, sortOrder: maxOrder + 1 });
  };

  const handleEditJob = (item) => {
    setJobFormState({ dayNumber: item.day_number, sortOrder: item.sort_order, existingItem: item });
  };

  const handleJobSaved = () => {
    setJobFormState(null);
    fetchData();
  };

  // ─── Add/Edit Transit handlers ─────────────────────

  const getHomeBaseForRoute = () => {
    if (!route?.applicator_id) return null;
    const app = applicators.find((a) => a.id === route.applicator_id);
    return app?.home_base_location || null;
  };

  const handleAddTransit = (dayNumber, category = 'transit') => {
    const dayItems = days[dayNumber] || [];
    const maxOrder = dayItems.reduce((max, i) => Math.max(max, i.sort_order || 0), 0);
    const nextSortOrder = maxOrder + 1;
    const priorEndpoint = findPriorEndpoint(dayItems, nextSortOrder, getHomeBaseForRoute());
    setTransitFormState({ dayNumber, sortOrder: nextSortOrder, priorEndpoint, category });
  };

  const handleEditTransit = (item) => {
    const dayItems = days[item.day_number] || [];
    const priorEndpoint = findPriorEndpoint(dayItems, item.sort_order, getHomeBaseForRoute());
    setTransitFormState({
      dayNumber: item.day_number,
      sortOrder: item.sort_order,
      priorEndpoint,
      existingItem: item,
    });
  };

  const handleTransitSaved = () => {
    // A new/edited Transit can change the day's effective endpoint, so mark stale
    if (transitFormState?.dayNumber) {
      setStaleTravelDays((prev) => {
        const next = new Set(prev);
        next.add(transitFormState.dayNumber);
        return next;
      });
    }
    setTransitFormState(null);
    fetchData();
  };

  // ─── Travel toggle handler (home ↔ hotel) ──────────

  const handleTravelToggle = async (travelItem, newType, customOrigin) => {
    const isToHotel = newType === 'start_of_day_hotel' || newType === 'end_of_day_hotel';

    if (isToHotel) {
      // Switching to hotel: flat 25 min estimate, update immediately
      const hotelMinutes = 25;
      const hotelHours = parseFloat((hotelMinutes / 60).toFixed(2));
      const isStart = newType === 'start_of_day_hotel';
      const desc = isStart
        ? `Hotel → ${travelItem.travel_description.split('→')[1]?.trim() || 'first job'} (~${hotelMinutes} min)`
        : `${travelItem.travel_description.split('→')[0]?.trim() || 'last job'} → Hotel (~${hotelMinutes} min)`;

      setItems(prev => prev.map(i => i.id === travelItem.id ? {
        ...i, travel_type: newType, travel_hours: hotelHours,
        travel_distance_km: null, travel_description: desc, travel_origin_override: null,
      } : i));

      await supabase.from('route_items').update({
        travel_type: newType, travel_hours: hotelHours,
        travel_distance_km: null, travel_description: desc, travel_origin_override: null,
      }).eq('id', travelItem.id);
    } else {
      // Switching to home base: need Google Maps calculation
      let homeBase = null;
      if (route.applicator_id) {
        const app = applicators.find(a => a.id === route.applicator_id);
        homeBase = app?.home_base_location || null;
      }
      const address = customOrigin || homeBase;
      const isEndOfDay = newType === 'end_of_day_home';

      if (!address) {
        // No home base set — just update type with placeholder
        const placeholderDesc = isEndOfDay
          ? '... → Home Base (set applicator home base or edit address)'
          : 'Home Base → ... (set applicator home base or edit address)';
        setItems(prev => prev.map(i => i.id === travelItem.id ? {
          ...i, travel_type: newType, travel_description: placeholderDesc,
          travel_hours: 0.5, travel_origin_override: customOrigin || null,
        } : i));
        await supabase.from('route_items').update({
          travel_type: newType, travel_hours: 0.5,
          travel_description: placeholderDesc,
          travel_origin_override: customOrigin || null,
        }).eq('id', travelItem.id);
        return;
      }

      // Mark as recalculating optimistically
      setItems(prev => prev.map(i => i.id === travelItem.id ? {
        ...i, travel_type: newType, travel_description: 'Calculating...', travel_origin_override: customOrigin || null,
      } : i));

      // Mark this day stale and trigger recalculation
      setStaleTravelDays(prev => {
        const next = new Set(prev);
        next.add(travelItem.day_number);
        return next;
      });

      // Save the new type + origin override immediately so recalc picks it up
      await supabase.from('route_items').update({
        travel_type: newType,
        travel_origin_override: customOrigin || null,
      }).eq('id', travelItem.id);

      // Trigger full recalc for this day
      fetchData();
    }
  };

  // ─── Move day to another route ──────────────────────

  const handleMoveDay = (dayNumber) => {
    const dayItems = days[dayNumber] || [];
    const movableItems = dayItems.filter(i => i.row_type === 'JOB' || i.row_type === 'TRANSIT');
    if (movableItems.length === 0) return;
    if (dayItems.some(i => i.is_pinned)) {
      alert('Unpin all jobs on this day before moving it to another route.');
      return;
    }
    setMoveDayState({ dayNumber, jobCount: movableItems.filter(i => i.row_type === 'JOB').length });
  };

  const handleMoveDayConfirm = async (targetRouteId, targetDayNumber) => {
    if (!moveDayState) return;
    const { dayNumber } = moveDayState;

    // 1. Delete TRAVEL rows for this day (auto-generated; will be re-derived on target route)
    await supabase.from('route_items').delete()
      .eq('route_id', routeId).eq('row_type', 'TRAVEL').eq('day_number', dayNumber);

    // 2. Get max sort_order on the target day (to append)
    const { data: targetItems } = await supabase.from('route_items')
      .select('sort_order').eq('route_id', targetRouteId).eq('day_number', targetDayNumber)
      .order('sort_order', { ascending: false }).limit(1);
    let nextOrder = (targetItems?.[0]?.sort_order || 0) + 1;

    // 3. Move all JOB and TRANSIT items to the target route (preserve relative order)
    const movableItems = (days[dayNumber] || [])
      .filter(i => i.row_type === 'JOB' || i.row_type === 'TRANSIT')
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    for (const item of movableItems) {
      await supabase.from('route_items').update({
        route_id: targetRouteId,
        day_number: targetDayNumber,
        sort_order: nextOrder++,
        day_override_hours: null, // clear extension on moved items
      }).eq('id', item.id);
    }

    // 4. Compact source route: close the gap
    const laterItems = items.filter(i => i.day_number > dayNumber);
    for (const item of laterItems) {
      await supabase.from('route_items').update({ day_number: item.day_number - 1 }).eq('id', item.id);
    }

    // 5. Mark adjacent days stale
    setStaleTravelDays(prev => {
      const next = new Set(prev);
      if (dayNumber > 1) next.add(dayNumber - 1);
      next.add(dayNumber);
      return next;
    });

    setMoveDayState(null);
    fetchData();
  };

  // ─── Route optimization handlers ────────────────────

  const handleOptimizeRoute = async () => {
    if (optimizing) return;
    const jobItems = items.filter(i => i.row_type === 'JOB');
    if (jobItems.length < 2) return;

    let homeBase = null;
    if (route.applicator_id) {
      const app = applicators.find(a => a.id === route.applicator_id);
      if (app?.home_base_location) {
        homeBase = { address: app.home_base_location };
      }
    }
    if (!homeBase) {
      alert('Set an applicator with a home base address before optimizing.');
      return;
    }

    setOptimizing(true);
    setTravelProgress({ current: 0, total: 0, description: 'Starting optimization...' });

    try {
      // Collect day limits (extended days)
      const dayLimitsMap = {};
      for (const item of items) {
        if (item.day_override_hours && item.day_number) {
          dayLimitsMap[item.day_number] = parseFloat(item.day_override_hours);
        }
      }

      const assignments = await optimizeRoute(
        jobItems, homeBase, optimizeStrategy, dayLimitsMap, 8,
        (current, total, desc) => setTravelProgress({ current, total, description: desc })
      );

      // Delete all travel rows (will be recalculated)
      await supabase.from('route_items').delete().eq('route_id', routeId).eq('row_type', 'TRAVEL');

      // Apply new assignments
      setTravelProgress({ current: 0, total: assignments.length, description: 'Applying optimized order...' });
      for (let i = 0; i < assignments.length; i++) {
        const a = assignments[i];
        await supabase.from('route_items')
          .update({ day_number: a.day_number, sort_order: a.sort_order })
          .eq('id', a.id);
        setTravelProgress({ current: i + 1, total: assignments.length, description: 'Applying optimized order...' });
      }

      // Mark all days stale
      const allDays = new Set(assignments.map(a => a.day_number));
      setStaleTravelDays(allDays);

      setDragNotice(`Route optimized (${optimizeStrategy === 'farthest_first' ? 'farthest first' : 'sequential from base'})`);
      setTimeout(() => setDragNotice(null), 4000);

      fetchData();
    } catch (err) {
      console.error('Route optimization failed:', err);
      setTravelProgress({ current: 0, total: 0, description: `Error: ${err.message}` });
      setTimeout(() => setTravelProgress(null), 5000);
    }

    setOptimizing(false);
    setTravelProgress(null);
  };

  const handleOptimizeDay = async (dayNumber) => {
    if (optimizing) return;
    const dayJobs = (days[dayNumber] || []).filter(i => i.row_type === 'JOB');
    if (dayJobs.length < 2) return;

    let homeBase = null;
    if (route.applicator_id) {
      const app = applicators.find(a => a.id === route.applicator_id);
      homeBase = app?.home_base_location || null;
    }

    // Determine start/end for this day — home base on the route's first/last day, else hotel
    const allDayNums = [...new Set(items.filter(i => i.row_type === 'JOB').map(i => i.day_number))];
    const firstRouteDay = allDayNums.length > 0 ? Math.min(...allDayNums) : null;
    const lastRouteDay = allDayNums.length > 0 ? Math.max(...allDayNums) : null;
    const isFirstDay = dayNumber === firstRouteDay;
    const isLastDay = dayNumber === lastRouteDay;

    const startPoint = (isFirstDay && homeBase) ? homeBase : (dayJobs[0]?.location || homeBase || 'BC, Canada');
    const endPoint = (isLastDay && homeBase) ? homeBase : (dayJobs[dayJobs.length - 1]?.location || 'BC, Canada');

    setOptimizing(true);
    try {
      const result = await optimizeDay(dayJobs, startPoint, endPoint);

      // Apply
      for (const r of result) {
        await supabase.from('route_items').update({ sort_order: r.sort_order }).eq('id', r.id);
      }

      setStaleTravelDays(prev => { const n = new Set(prev); n.add(dayNumber); return n; });
      setDragNotice(`Day ${dayNumber} optimized`);
      setTimeout(() => setDragNotice(null), 3000);
      fetchData();
    } catch (err) {
      console.error(`Day ${dayNumber} optimization failed:`, err);
      alert(`Optimization failed: ${err.message}`);
    }
    setOptimizing(false);
  };

  // ─── Day swap handler (drag entire day) ─────────────

  const handleDaySwap = async (sourceDayNum, targetDayNum) => {
    if (sourceDayNum === targetDayNum) return;

    // Block if either day has pinned items
    const sourceItems = items.filter(i => i.day_number === sourceDayNum);
    const targetItems = items.filter(i => i.day_number === targetDayNum);
    if (sourceItems.some(i => i.is_pinned) || targetItems.some(i => i.is_pinned)) return;

    // Optimistic update
    setItems(prev => prev.map(i => {
      if (i.day_number === sourceDayNum) return { ...i, day_number: targetDayNum };
      if (i.day_number === targetDayNum) return { ...i, day_number: sourceDayNum };
      return i;
    }));

    // Mark both days stale
    setStaleTravelDays(prev => {
      const next = new Set(prev);
      next.add(sourceDayNum);
      next.add(targetDayNum);
      return next;
    });

    // Toast
    setDragNotice(`Swapped Day ${sourceDayNum} ↔ Day ${targetDayNum}`);
    setTimeout(() => setDragNotice(null), 3000);

    // Write to DB
    const updates = [
      ...sourceItems.map(i => ({ id: i.id, day_number: targetDayNum })),
      ...targetItems.map(i => ({ id: i.id, day_number: sourceDayNum })),
    ];
    for (const u of updates) {
      await supabase.from('route_items').update({ day_number: u.day_number }).eq('id', u.id);
    }
  };

  // ─── Drag and drop handler ─────────────────────────

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // ── Day-level swap ──
    if (activeId.startsWith('day-swap-') && overId.startsWith('day-swap-')) {
      const sourceDayNum = parseInt(activeId.replace('day-swap-', ''));
      const targetDayNum = parseInt(overId.replace('day-swap-', ''));
      await handleDaySwap(sourceDayNum, targetDayNum);
      return;
    }

    // ── Item-level drag (existing logic) ──
    const activeItem = items.find((i) => i.id === active.id);
    if (!activeItem || activeItem.is_pinned) return;

    // Handle drop onto empty day zone
    if (overId.startsWith('day-drop-')) {
      const targetDayNum = parseInt(overId.replace('day-drop-', ''));
      const sourceDayNum = activeItem.day_number;
      if (sourceDayNum === targetDayNum) return;

      // Move item to the target empty day
      const newItems = items.map((i) =>
        i.id === activeItem.id
          ? { ...i, day_number: targetDayNum, sort_order: 1, _justMoved: true }
          : i
      );

      // Show toast
      const dayLabel = formatDate(route.start_date, targetDayNum);
      setDragNotice(`Moved ${activeItem.company || 'job'} to Day ${targetDayNum} (${dayLabel})`);
      setTimeout(() => setDragNotice(null), 3000);

      setItems(newItems);
      setStaleTravelDays((prev) => {
        const next = new Set(prev);
        next.add(sourceDayNum);
        next.add(targetDayNum);
        return next;
      });

      // Re-number source day
      const sourceDayItems = newItems.filter((i) => i.day_number === sourceDayNum);
      const updates = [{ id: activeItem.id, day_number: targetDayNum, sort_order: 1 }];
      sourceDayItems.forEach((item, index) => {
        updates.push({ id: item.id, day_number: sourceDayNum, sort_order: index + 1 });
      });

      for (const update of updates) {
        await supabase.from('route_items')
          .update({ day_number: update.day_number, sort_order: update.sort_order })
          .eq('id', update.id);
      }
      setItems(prev => prev.map(i => i._justMoved ? { ...i, _justMoved: false } : i));
      return;
    }

    const overItem = items.find((i) => i.id === over.id);
    if (!overItem) return;

    const sourceDayNum = activeItem.day_number;
    const targetDayNum = overItem.day_number;

    // Create immutable copies to avoid mutating React state
    let newItems = items.map(i => i.id === active.id
      ? { ...i, day_number: targetDayNum, _justMoved: true }
      : { ...i }
    );
    const activeIndex = newItems.findIndex(i => i.id === active.id);
    let overIndex = newItems.findIndex(i => i.id === over.id);
    const movedItem = newItems[activeIndex];

    // TRAVEL rows are non-droppable, so over.id is the nearest JOB/TRANSIT.
    // Look at the active's translated rect vs over's rect to decide which side
    // of that job the cursor actually landed on — otherwise dropping in the
    // visual band of an auto-travel row would always snap above the adjacent job.
    const overRect = over.rect;
    const activeRect = active.rect.current?.translated ?? active.rect.current?.initial;
    if (overRect && activeRect) {
      const activeMidY = activeRect.top + activeRect.height / 2;
      const overMidY = overRect.top + overRect.height / 2;
      if (activeMidY > overMidY) overIndex += 1;
    }

    newItems = arrayMove(newItems, activeIndex, overIndex);

    const affectedDays = new Set([sourceDayNum, targetDayNum]);
    const updates = [];

    for (const dayNum of affectedDays) {
      newItems = newItems.map((item, _, arr) => {
        if (item.day_number !== dayNum) return item;
        const dayItems = arr.filter(i => i.day_number === dayNum);
        const idx = dayItems.findIndex(i => i.id === item.id);
        const newOrder = idx + 1;
        updates.push({ id: item.id, day_number: item.day_number, sort_order: newOrder });
        return item.sort_order !== newOrder ? { ...item, sort_order: newOrder } : item;
      });
    }

    // Show toast for cross-day moves
    if (sourceDayNum !== targetDayNum) {
      const targetWeek = getWeekNumber(route.start_date, targetDayNum);
      const sourceWeek = getWeekNumber(route.start_date, sourceDayNum);
      const dayLabel = formatDate(route.start_date, targetDayNum);
      const msg = sourceWeek !== targetWeek
        ? `Moved ${movedItem.company || 'job'} to Week ${targetWeek}, Day ${targetDayNum}`
        : `Moved ${movedItem.company || 'job'} to Day ${targetDayNum} (${dayLabel})`;
      setDragNotice(msg);
      setTimeout(() => setDragNotice(null), 3000);
    }

    setItems(newItems);

    // Mark affected days as having stale travel data
    setStaleTravelDays((prev) => {
      const next = new Set(prev);
      affectedDays.forEach((d) => next.add(d));
      return next;
    });

    for (const update of updates) {
      await supabase
        .from('route_items')
        .update({ day_number: update.day_number, sort_order: update.sort_order })
        .eq('id', update.id);
    }

    // Clear the blink after DB writes complete
    setItems(prev => prev.map(i => i._justMoved ? { ...i, _justMoved: false } : i));
  };

  // ─── Recalculate Travel handler ─────────────────────
  // Marks all travel rows on stale days for agent recalculation.
  // The agent (via Supabase MCP) will delete old TRAVEL rows and
  // insert new ones with updated drive times/distances.

  const handleRecalculateTravel = async () => {
    setRecalculating(true);
    // If no stale days, calculate all days
    const staleDayNumbers = staleTravelDays.size > 0
      ? [...staleTravelDays]
      : [...new Set(items.map((i) => i.day_number))];

    // 0. Preserve user overrides (toggle state + custom addresses) before deleting
    const dayOverrides = {};
    for (const item of items) {
      if (item.row_type !== 'TRAVEL' || !staleDayNumbers.includes(item.day_number)) continue;
      const dn = item.day_number;
      if (!dayOverrides[dn]) dayOverrides[dn] = {};
      if (item.travel_type === 'start_of_day_home' || item.travel_type === 'start_of_day_hotel') {
        dayOverrides[dn].startType = item.travel_type === 'start_of_day_home' ? 'home' : 'hotel';
        if (item.travel_origin_override) dayOverrides[dn].startOrigin = item.travel_origin_override;
      }
      if (item.travel_type === 'end_of_day_home' || item.travel_type === 'end_of_day_hotel') {
        dayOverrides[dn].endType = item.travel_type === 'end_of_day_home' ? 'home' : 'hotel';
        if (item.travel_origin_override) dayOverrides[dn].endDestination = item.travel_origin_override;
      }
      // Legacy types: map old travel_type values to new override format
      if (item.travel_type === 'home_base' || item.travel_type === 'start_of_week_from_home') {
        dayOverrides[dn].startType = 'home';
      }
      if (item.travel_type === 'end_of_week_home') {
        dayOverrides[dn].endType = 'home';
      }
    }

    // 1. Delete existing TRAVEL rows on target days
    if (staleTravelDays.size === 0) {
      await supabase
        .from('route_items')
        .delete()
        .eq('route_id', routeId)
        .eq('row_type', 'TRAVEL');
    } else {
      for (const dayNum of staleDayNumbers) {
        await supabase
          .from('route_items')
          .delete()
          .eq('route_id', routeId)
          .eq('row_type', 'TRAVEL')
          .eq('day_number', dayNum);
      }
    }

    // 2. Try client-side Google Maps calculation
    const hasGoogleMapsKey = !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (hasGoogleMapsKey) {
      try {
        setTravelProgress({ current: 0, total: 0, description: 'Loading route data...' });

        // Get fresh items without travel rows
        const { data: rawFreshItems } = await supabase
          .from('route_items')
          .select('*, crm_job:crm_jobs(*)')
          .eq('route_id', routeId)
          .order('day_number', { ascending: true })
          .order('sort_order', { ascending: true });
        const freshItems = flattenCrmJobs(rawFreshItems);

        // Get applicator home base
        let homeBase = null;
        if (route.applicator_id) {
          const { data: app } = await supabase
            .from('applicators')
            .select('home_base_location')
            .eq('id', route.applicator_id)
            .single();
          if (app) {
            homeBase = app.home_base_location;
          }
        }

        const jobCount = (freshItems || []).filter(i => i.row_type === 'JOB' && i.location).length;
        setTravelProgress({ current: 0, total: jobCount, description: 'Calculating travel...' });

        const travelResults = await calculateTravelForDays(freshItems || [], staleDayNumbers, {
          startDate: route.start_date,
          homeBase,
          dayOverrides,
          onProgress: (current, total, desc) => {
            setTravelProgress({ current, total, description: desc });
          },
        });

        // Build interleaved list per day: jobs + travel rows in correct order
        // Then insert travel rows with proper integer sort_orders and update job sort_orders
        console.log(`[Travel] Got ${travelResults.length} travel results`);
        setTravelProgress({ current: 0, total: travelResults.length, description: 'Saving travel rows...' });

        // Group travel results by day
        const travelByDay = {};
        for (const t of travelResults) {
          if (!travelByDay[t.day_number]) travelByDay[t.day_number] = [];
          travelByDay[t.day_number].push(t);
        }

        let insertCount = 0;
        for (const dayNum of staleDayNumbers) {
          // Anchors include JOBs and TRANSIT rows — both keep relative position during regen.
          // TRANSIT is opaque to auto-travel: inter_job travel is still computed JOB→JOB,
          // so TRANSIT rows simply preserve their slots in the timeline.
          const dayAnchors = (freshItems || [])
            .filter(i => i.day_number === dayNum && (i.row_type === 'JOB' || i.row_type === 'TRANSIT'))
            .sort((a, b) => a.sort_order - b.sort_order);
          const dayTravel = travelByDay[dayNum] || [];

          // Build interleaved order: for each anchor, check if a travel row goes before it
          const ordered = [];
          for (const anchor of dayAnchors) {
            for (const t of dayTravel) {
              if (t.sort_order_after < anchor.sort_order && !t._placed) {
                ordered.push({ type: 'travel', data: t });
                t._placed = true;
              }
            }
            ordered.push({ type: anchor.row_type === 'JOB' ? 'job' : 'transit', data: anchor });
          }
          // Append any remaining travel rows (end-of-day)
          for (const t of dayTravel) {
            if (!t._placed) {
              ordered.push({ type: 'travel', data: t });
            }
          }

          // Now assign sequential sort_orders and write to DB
          for (let i = 0; i < ordered.length; i++) {
            const sortOrder = i + 1;
            const entry = ordered[i];

            if (entry.type === 'travel') {
              insertCount++;
              setTravelProgress({ current: insertCount, total: travelResults.length, description: `Saving: ${entry.data.travel_description}` });
              const { error: insertErr } = await supabase.from('route_items').insert([{
                route_id: routeId,
                row_type: 'TRAVEL',
                day_number: dayNum,
                sort_order: sortOrder,
                travel_description: entry.data.travel_description,
                travel_hours: entry.data.travel_hours,
                travel_distance_km: entry.data.travel_distance_km,
                travel_type: entry.data.travel_type,
                travel_origin_override: entry.data.travel_origin_override || null,
              }]);
              if (insertErr) console.error('[Travel] Insert failed:', insertErr);
              else console.log(`[Travel] Inserted: Day ${dayNum}, order ${sortOrder}, ${entry.data.travel_description}`);
            } else if (entry.data.sort_order !== sortOrder) {
              // Update existing JOB or TRANSIT sort_order if it shifted
              await supabase.from('route_items')
                .update({ sort_order: sortOrder })
                .eq('id', entry.data.id);
            }
          }
        }
        console.log(`[Travel] Done. Inserted ${insertCount} travel rows.`);

        // 2b. Refresh unlocked TRANSIT rows' hours/distance from current prior endpoints.
        // (Locked rows preserve user-entered hours — e.g. plane/ferry segments.)
        const transitRows = (freshItems || []).filter(
          (i) => i.row_type === 'TRANSIT' && staleDayNumbers.includes(i.day_number) && !i.travel_hours_locked && i.category !== 'other'
        );
        for (const transit of transitRows) {
          const sameDayItems = (freshItems || []).filter((i) => i.day_number === transit.day_number);
          const priorEndpoint = findPriorEndpoint(sameDayItems, transit.sort_order, homeBase);
          try {
            const result = await computeTransitTravel({ priorEndpoint, transit });
            if (result) {
              await supabase.from('route_items').update({
                travel_hours: result.travel_hours,
                travel_distance_km: result.travel_distance_km,
                travel_description: result.travel_description,
              }).eq('id', transit.id);
            }
          } catch (err) {
            console.warn(`[Transit recalc] Failed for ${transit.id}:`, err.message);
          }
        }

        // Verify: re-fetch and check travel rows exist
        const { data: verifyItems } = await supabase
          .from('route_items')
          .select('row_type')
          .eq('route_id', routeId)
          .eq('row_type', 'TRAVEL');
        const dbTravelCount = verifyItems?.length || 0;
        console.log(`[Travel] Verification: ${dbTravelCount} TRAVEL rows in DB (expected ${insertCount})`);
        if (dbTravelCount === 0 && insertCount > 0) {
          console.warn('[Travel] WARNING: Inserts reported success but no TRAVEL rows found in DB!');
        }

        // Recalc done client-side — clear the flag
        setStaleTravelDays(new Set());
        await supabase.from('routes').update({ needs_travel_calc: false }).eq('id', routeId);
        setRecalculating(false);
        setTravelProgress(null);
        fetchData();
        return;
      } catch (err) {
        console.error('Travel calc failed:', err);
        setRecalculating(false);
        setTravelProgress({ current: 0, total: 0, description: `Error: ${err.message}` });
        setTimeout(() => setTravelProgress(null), 5000);
      }
    }

    // 3. Fallback: signal the agent to handle it
    setStaleTravelDays(new Set());
    await supabase
      .from('routes')
      .update({ needs_travel_calc: true })
      .eq('id', routeId);

    setRecalculating(false);
    fetchData();
  };

  // ─── Route status handler ─────────────────────────

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    setRoute((prev) => ({ ...prev, status: newStatus }));

    const { error } = await supabase
      .from('routes')
      .update({ status: newStatus })
      .eq('id', routeId);

    if (error) {
      console.error('Error updating status:', error);
      setStatusUpdating(false);
      fetchData();
      return;
    }

    // When cancelling a route, delete all route_items so jobs return to unassigned pool
    if (newStatus === 'cancelled') {
      await supabase
        .from('route_items')
        .delete()
        .eq('route_id', routeId);
    }

    setStatusUpdating(false);
    fetchData();
  };

  const STATUS_TRANSITIONS = {
    draft: ['active', 'cancelled'],
    active: ['completed', 'cancelled'],
    completed: ['active'],
    cancelled: ['draft'],
  };

  // ─── Render ─────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-gray-300">
        <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-green-600 rounded-full"></div>
        Loading route data...
      </div>
    );
  }

  if (!route) {
    return (
      <div>
        <Link to="/portal/routes" className="text-green-400 hover:underline text-sm">&larr; Back to routes</Link>
        <p className="text-gray-300 mt-4">Route not found.</p>
      </div>
    );
  }

  const totalValue = items
    .filter((i) => i.row_type === 'JOB' && i.value)
    .reduce((sum, i) => sum + parseFloat(i.value), 0);

  const totalJobHours = items
    .filter((i) => i.row_type === 'JOB')
    .reduce((sum, i) => sum + (parseFloat(i.estimated_hours) || 0), 0);

  const totalTravelHours = items
    .filter((i) => i.row_type === 'TRAVEL' || i.row_type === 'TRANSIT')
    .reduce((sum, i) => sum + (parseFloat(i.travel_hours) || 0), 0);

  // Lay the route's days onto calendar weeks running Sun→Sat. Each week has 7 slots
  // (Sun..Sat). Days before the route's start_date (e.g. the Sunday ahead of a Monday
  // start) render as blank "pre-start" cells; days past the last work day fill out to
  // Saturday as normal empty columns. A slot is { dayNumber } or { preStartOffset }.
  const startDow = route.start_date ? new Date(route.start_date + 'T00:00:00').getDay() : 0;
  const maxDayFromItems = dayNumbers.length > 0 ? Math.max(...dayNumbers) : 1;
  const lastWeekIdx = Math.floor((startDow + Math.max(maxDayFromItems, 1) - 1) / 7);
  const weeks = {};
  for (let w = 0; w <= lastWeekIdx; w++) {
    const slots = [];
    for (let col = 0; col < 7; col++) {
      const dayNum = w * 7 + col - startDow + 1;
      slots.push(dayNum >= 1 ? { dayNumber: dayNum } : { preStartOffset: 1 - dayNum });
    }
    weeks[w + 1] = slots;
  }
  const weekNumbers = Object.keys(weeks).map(Number).sort((a, b) => a - b);

  return (
    <JobExpandHintProvider>
    <div>
      {/* Header */}
      <div className={`mb-6 ${showUnassigned ? 'lg:mr-80' : ''} transition-all`}>
        <Link to="/portal/routes" className="text-green-400 hover:underline text-sm">&larr; Back to routes</Link>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-2 gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-white truncate">{route.route_name}</h1>
              <button
                onClick={() => setShowEditRoute(true)}
                className="text-gray-400 hover:text-white transition-colors shrink-0"
                title="Edit route"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-sm text-gray-300">
              {route.corridor && <span>{route.corridor}</span>}
              {route.start_date && (
                <span>
                  Starts{' '}
                  {new Date(route.start_date + 'T00:00:00').toLocaleDateString('en-CA', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              )}
              <span className="text-green-400 font-medium">${totalValue.toLocaleString()}</span>
              <span className="text-gray-400">{items.filter((i) => i.row_type === 'JOB').length} jobs</span>
              <span className="text-gray-400">{totalJobHours.toFixed(1)}h work + {totalTravelHours.toFixed(1)}h travel</span>
              <span className="text-gray-400">{dayNumbers.length} days / {weekNumbers.length} wk</span>
              {/* Applicator assignment */}
              <select
                value={route.applicator_id || ''}
                onChange={(e) => handleApplicatorChange(e.target.value)}
                className="text-xs bg-gray-700 border border-gray-500 text-gray-300 rounded px-2 py-0.5 focus:ring-1 focus:ring-green-600 focus:outline-none"
              >
                <option value="">No applicator</option>
                {applicators.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              {staleTravelDays.size > 0 && (
                <span className="text-amber-400 text-xs">
                  Travel stale on {staleTravelDays.size} day(s)
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {route.start_date && ['draft', 'active'].includes(route.status) && (
              <button
                onClick={() => setShowPushRoute(true)}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-gray-700 text-gray-300 hover:text-white transition-colors"
              >
                Push Route
              </button>
            )}
            <button
              onClick={handleRecalculateTravel}
              disabled={recalculating}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                staleTravelDays.size > 0
                  ? 'bg-amber-400/20 text-amber-300 hover:bg-amber-400/30'
                  : 'bg-gray-700 text-gray-300 hover:text-white'
              }`}
            >
              {recalculating ? 'Calculating...' : staleTravelDays.size > 0 ? 'Recalculate Travel' : 'Calculate Travel'}
            </button>

            {/* Optimize Route button + strategy picker */}
            <span className="inline-flex items-center gap-0 shrink-0">
              <button
                onClick={handleOptimizeRoute}
                disabled={optimizing || recalculating}
                className="px-3 py-1.5 text-xs font-medium bg-gray-700 text-gray-300 hover:text-white rounded-l-md transition-colors disabled:opacity-50"
              >
                {optimizing ? 'Optimizing...' : 'Optimize Route'}
              </button>
              <select
                value={optimizeStrategy}
                onChange={(e) => setOptimizeStrategy(e.target.value)}
                disabled={optimizing}
                className="py-1.5 pl-1 pr-5 text-xs bg-gray-700 text-gray-300 border-l border-gray-500/30 rounded-r-md focus:outline-none focus:ring-1 focus:ring-green-600 disabled:opacity-50"
              >
                <option value="farthest_first">Farthest First</option>
                <option value="sequential">Sequential</option>
              </select>
            </span>

            {/* Travel calculation progress */}
            {travelProgress && (
              <div className="flex items-center gap-2 text-xs text-gray-400 max-w-xs">
                <div className="w-20 bg-gray-700 rounded-full h-1.5 shrink-0">
                  <div
                    className="bg-green-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${travelProgress.total > 0 ? (travelProgress.current / travelProgress.total) * 100 : 0}%` }}
                  />
                </div>
                <span className={`truncate ${travelProgress.description.startsWith('Error') ? 'text-red-200' : ''}`}>
                  {travelProgress.description}
                </span>
              </div>
            )}
            <button
              onClick={() => setShowUnassigned(!showUnassigned)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                showUnassigned
                  ? 'bg-green-700 text-white'
                  : 'bg-gray-700 text-gray-300 hover:text-white'
              }`}
            >
              {showUnassigned ? 'Hide' : 'Unassigned'} Jobs
            </button>
            {/* Status transition buttons */}
            {(STATUS_TRANSITIONS[route.status] || []).map((nextStatus) => (
              <button
                key={nextStatus}
                onClick={() => handleStatusChange(nextStatus)}
                disabled={statusUpdating}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                  nextStatus === 'active' ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30' :
                  nextStatus === 'completed' ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30' :
                  nextStatus === 'cancelled' ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30' :
                  'bg-gray-500/20 text-gray-300 hover:bg-gray-500/30'
                }`}
              >
                Mark {nextStatus}
              </button>
            ))}
            <span
              className={`px-3 py-1 text-sm font-medium text-white rounded-full ${
                { draft: 'bg-gray-500', active: 'bg-green-600', completed: 'bg-blue-500', cancelled: 'bg-red-500' }[route.status] || 'bg-gray-500'
              }`}
            >
              {route.status}
            </span>
          </div>
        </div>
        {route.notes && <p className="text-sm text-gray-400 mt-2">{route.notes}</p>}

        {/* Drag move toast */}
        {dragNotice && (
          <div className="mt-2 px-3 py-1.5 bg-green-600/20 border border-green-600/30 rounded-md text-sm text-green-400 inline-block">
            {dragNotice}
          </div>
        )}
        {route.needs_travel_calc && (
          <div className="mt-3 px-3 py-2 bg-amber-400/10 border border-amber-400/30 rounded-md flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
            <span className="text-sm text-amber-300">Travel times need recalculation — click "Calculate Travel" above to update.</span>
          </div>
        )}
      </div>

      {/* Day columns grouped by week */}
      <div className={`${showUnassigned ? 'lg:mr-80' : ''} transition-all`}>
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {weekNumbers.map((weekNum) => {
          const slots = weeks[weekNum];
          const realDayNums = slots.filter((s) => s.dayNumber).map((s) => s.dayNumber);
          const weekItems = realDayNums.flatMap((d) => (days[d] || []));
          // Calendar Sun→Sat span for the week label (Sunday sits `startDow` days before day 1).
          const sundayDayNum = (weekNum - 1) * 7 - startDow + 1;
          return (
            <WeekSection
              key={weekNum}
              weekNumber={weekNum}
              dateRange={formatDateRange(route.start_date, [sundayDayNum, sundayDayNum + 6])}
              items={weekItems}
              applicator={route.applicator_id ? applicators.find(a => a.id === route.applicator_id) : null}
              entryForDay={entryForDay}
            >
              <SortableContext items={realDayNums.map(d => `day-swap-${d}`)} strategy={verticalListSortingStrategy}>
              {slots.map((slot, idx) => (
                slot.dayNumber ? (
                <DayColumn
                  key={`d${slot.dayNumber}`}
                  dayNumber={slot.dayNumber}
                  items={days[slot.dayNumber] || []}
                  route={route}
                  isStaleTravel={staleTravelDays.has(slot.dayNumber)}
                  onTogglePin={handleTogglePin}
                  onEditJob={handleEditJob}
                  onEditTransit={handleEditTransit}
                  onDeleteItem={(item) => setDeleteItemState({ id: item.id, company: item.company || item.travel_description || 'Travel segment', isTransit: item.row_type === 'TRANSIT' })}
                  onAddJob={handleAddJob}
                  onAddTransit={handleAddTransit}
                  onCancelDay={handleCancelDayStart}
                  onMoveDay={handleMoveDay}
                  onOptimizeDay={handleOptimizeDay}
                  onSetDayExtension={handleSetDayExtension}
                  holidays={holidays}
                  onTravelToggle={handleTravelToggle}
                  homeBase={route.applicator_id ? (applicators.find(a => a.id === route.applicator_id)?.home_base_location || null) : null}
                  entry={entryForDay(slot.dayNumber)}
                  onOpenClock={handleOpenClock}
                  hoursEnabled={hoursEnabled}
                  onOpenNotes={handleOpenNotes}
                />
                ) : (
                <BlankDayCell
                  key={`pre${idx}`}
                  startDate={route.start_date}
                  preStartOffset={slot.preStartOffset}
                  onInclude={handleIncludeLeadingDay}
                />
                )
              ))}
              </SortableContext>
            </WeekSection>
          );
        })}
        <DragOverlay dropAnimation={null}>
          {activeDragId ? (() => {
            const dragId = String(activeDragId);
            if (dragId.startsWith('day-swap-')) {
              const dayNum = parseInt(dragId.replace('day-swap-', ''));
              const dayItems = (days[dayNum] || []).filter(i => i.row_type === 'JOB');
              return (
                <div className="opacity-90 shadow-xl bg-gray-700 rounded-lg p-3 border border-green-600/40">
                  <div className="text-white font-semibold text-sm">Day {dayNum}</div>
                  <div className="text-gray-400 text-xs">{dayItems.length} job{dayItems.length !== 1 ? 's' : ''}</div>
                </div>
              );
            }
            const dragItem = items.find((i) => i.id === activeDragId);
            if (!dragItem) return null;
            return (
              <div className="opacity-90 shadow-xl rounded-lg">
                <ItemCard item={dragItem} route={route} onTogglePin={() => {}} onEditJob={() => {}} onEditTransit={() => {}} isStaleTravel={false} />
              </div>
            );
          })() : null}
        </DragOverlay>
      </DndContext>

      {dayNumbers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400 text-lg">No items in this route yet.</p>
          <p className="text-gray-400 text-sm mt-1">
            Items will appear here once the Claude agent builds this route or you add jobs manually.
          </p>
        </div>
      )}
      </div>

      {/* Unassigned Jobs Panel */}
      <UnassignedJobsPanel
        routeId={routeId}
        visible={showUnassigned}
        onClose={() => setShowUnassigned(false)}
        onAssignJob={fetchData}
      />

      {/* Cancel Day confirmation modal */}
      {cancelDayState && (
        <ConfirmModal
          title={`Cancel Day ${cancelDayState.dayNumber}?`}
          confirmLabel="Push Forward"
          onConfirm={handleCancelDayConfirm}
          onCancel={() => setCancelDayState(null)}
          destructive
        >
          <p>This will push <strong>{cancelDayState.jobCount}</strong> non-pinned item(s) forward by one day.</p>
          <p className="mt-1">All subsequent days will also shift forward.</p>
          {cancelDayState.pinnedCount > 0 && (
            <p className="mt-2 text-orange-300">
              <strong>{cancelDayState.pinnedCount}</strong> pinned item(s) will remain in place.
            </p>
          )}
        </ConfirmModal>
      )}

      {deleteItemState && (
        <ConfirmModal
          title={deleteItemState.isTransit ? 'Remove travel segment?' : 'Remove job from route?'}
          confirmLabel="Remove"
          onConfirm={async () => {
            const id = deleteItemState.id;
            const removed = items.find((i) => i.id === id);
            const dayNumber = removed?.day_number;
            // Optimistic removal so the row vanishes immediately
            setItems((prev) => prev.filter((i) => i.id !== id));
            if (dayNumber) {
              setStaleTravelDays((prev) => {
                const next = new Set(prev);
                next.add(dayNumber);
                return next;
              });
            }
            setDeleteItemState(null);
            // Background DB delete; reconcile with server on failure
            const { error } = await supabase.from('route_items').delete().eq('id', id);
            if (error) {
              console.error('Error removing item from route:', error);
              fetchData();
            }
          }}
          onCancel={() => setDeleteItemState(null)}
          destructive
        >
          {deleteItemState.isTransit ? (
            <p><strong>{deleteItemState.company}</strong> will be removed from this day.</p>
          ) : (
            <>
              <p><strong>{deleteItemState.company || 'This job'}</strong> will be removed from this route.</p>
              <p className="mt-2 text-gray-400 text-xs">The job stays in the CRM and will re-appear in the Unassigned Jobs panel.</p>
            </>
          )}
        </ConfirmModal>
      )}

      {/* Add/Edit job modal */}
      {jobFormState && (
        <JobFormModal
          routeId={routeId}
          dayNumber={jobFormState.dayNumber}
          sortOrder={jobFormState.sortOrder}
          existingItem={jobFormState.existingItem}
          onClose={() => setJobFormState(null)}
          onSaved={handleJobSaved}
        />
      )}

      {/* Add/Edit Transit modal */}
      {transitFormState && (
        <TransitFormModal
          routeId={routeId}
          dayNumber={transitFormState.dayNumber}
          sortOrder={transitFormState.sortOrder}
          priorEndpoint={transitFormState.priorEndpoint}
          existingItem={transitFormState.existingItem}
          category={transitFormState.category}
          onClose={() => setTransitFormState(null)}
          onSaved={handleTransitSaved}
        />
      )}

      {/* Move Day to Route modal */}
      {moveDayState && (
        <MoveDayModal
          routeId={routeId}
          dayNumber={moveDayState.dayNumber}
          jobCount={moveDayState.jobCount}
          onConfirm={handleMoveDayConfirm}
          onClose={() => setMoveDayState(null)}
        />
      )}

      {/* Edit Route modal */}
      {showEditRoute && (
        <EditRouteModal
          route={route}
          items={items}
          onClose={() => setShowEditRoute(false)}
          onSaved={handleEditRouteSaved}
        />
      )}

      {/* Push Route modal */}
      {showPushRoute && (
        <PushRouteModal
          route={route}
          items={items}
          holidays={holidays}
          onClose={() => setShowPushRoute(false)}
          onPushed={() => {
            setShowPushRoute(false);
            fetchData();
          }}
        />
      )}

      {/* Clock in / out modal (actual payroll hours) */}
      {clockState && (
        <ClockEntryModal
          route={route}
          dayNumber={clockState.dayNumber}
          mode={clockState.mode}
          workDate={clockState.workDate}
          startLocation={clockState.startLocation}
          endLocation={clockState.endLocation}
          existingEntry={clockState.existingEntry}
          onClose={() => setClockState(null)}
          onSaved={() => { setClockState(null); fetchTimeEntries(); }}
        />
      )}

      {/* Day notes modal (general note for the day) */}
      {notesState && (
        <DayNotesModal
          route={route}
          dayNumber={notesState.dayNumber}
          workDate={notesState.workDate}
          existingEntry={notesState.existingEntry}
          onClose={() => setNotesState(null)}
          onSaved={() => { setNotesState(null); fetchTimeEntries(); }}
        />
      )}
    </div>
    </JobExpandHintProvider>
  );
};

export default RouteDetailPage;
